import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button.js';
import {
  SecurityProcessMenu,
  MEDIUM_SECURITY_PAGES,
} from '../components/SecurityProcessMenu.js';
import { useCustomer } from '../customer-context.js';
import { api } from '../api/client.js';
import { isOtpLockedOut } from '../lib/action-denied.js';
import {
  beginHighSecurity,
  carryHighSecurity2fa,
  commitHighSecurity,
} from '../lib/high-security.js';
import infoIcon from '../assets/info-icon.svg';
import { useContent, useContentMap } from '../content.js';
import { countDistinctAnswers } from './registration-common.js';
import {
  availableSkip2faReasons as skip2faReasonsFor,
  requiresTroubleshootingConfirmation,
} from '../lib/skip-2fa-reasons.js';
import { SECURITY_RUN_SNAPSHOT_KEY } from '../security-run-snapshot.js';

/** Card / info-box wrapper matching the PTSB design-system `InfoBox`. */
function InfoBox({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={`bg-white border-2 border-[#dad7d3] p-6 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

type CheckStatus = 'pass' | 'fail' | null;

/**
 * Accessible pass/fail radio pair, faithful to the export's `AccessibleRadio`
 * (shared with the SCRN-03 Medium Security port).
 */
function PassFailRadios({
  name,
  value,
  onChange,
  disabled,
}: {
  name: string;
  value: CheckStatus;
  onChange: (v: 'pass' | 'fail') => void;
  /**
   * `true`/`false` disables both options equally. A per-option object allows
   * e.g. blocking Pass while still letting the agent record an explicit Fail
   * (issue #347: an upstream lookup error invites "Mark Fail if this cannot
   * be confirmed by another means" — Fail must stay clickable there, only
   * Pass is fail-closed).
   */
  disabled?: boolean | { pass?: boolean; fail?: boolean };
}): React.JSX.Element {
  return (
    <div
      className="flex items-center gap-6"
      role="radiogroup"
      aria-label={`${name} verification result`}
    >
      {(['pass', 'fail'] as const).map((option) => {
        const id = `${name}-${option}`;
        const checked = value === option;
        const isDisabled =
          typeof disabled === 'object' ? (disabled[option] ?? false) : disabled;
        return (
          <div key={option} className="flex items-center gap-3">
            <input
              type="radio"
              id={id}
              name={name}
              checked={checked}
              onChange={() => onChange(option)}
              disabled={isDisabled}
              className="sr-only peer"
            />
            <label
              htmlFor={id}
              className="relative flex items-center cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-50 group"
            >
              <div className="relative size-[24px] rounded-full border-2 border-[#979c9f] transition-all bg-white group-hover:border-[#6b7280] peer-focus-visible:outline peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#005fcc]">
                {checked && (
                  <div className="absolute inset-0 m-auto size-[12px] rounded-full bg-[#1d252c]" />
                )}
              </div>
              <span className="ml-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none capitalize">
                {option}
              </span>
            </label>
          </div>
        );
      })}
    </div>
  );
}

/** A labelled identity question with its (optional) value and pass/fail radios. */
function IdentityQuestion({
  label,
  value,
  listValue,
  hint,
  checkAction,
  name,
  status,
  onChange,
  notApplicable = false,
  unavailable = false,
  infoNote,
}: {
  label: string;
  /**
   * The on-file value. Omitted for an EXTERNAL (TSYS) check, which shows no
   * value — only the label + hint + Pass/Fail (issue #223 / CCF-5).
   */
  value?: string;
  /**
   * When the on-file answer has multiple entries (e.g. several joint
   * accounts or VISA cardholders), render each as its own list item instead
   * of one run-together `; `-separated string (issue #291 / CCF-26) — matches
   * the list-of-entries pattern used on `EclHighSecurityScreen.tsx`. Takes
   * precedence over `value` when non-empty.
   */
  listValue?: string[];
  /**
   * Grey sub-instruction beneath the label (e.g. "Check TSYS for …"). May
   * contain `\n` for a multi-line script (CCF-26, issue #307).
   */
  hint?: string;
  /** An in-app "Check …" button (CCF-26/CCF-47, issue #307) — see {@link SecurityQuestion.checkAction}. */
  checkAction?: { label: string; ariaLabel: string; onClick: () => void };
  name: string;
  status: CheckStatus;
  onChange: (v: 'pass' | 'fail') => void;
  /**
   * When true this question is NOT APPLICABLE (genuinely unanswerable — e.g.
   * EDS High joint accounts when the customer has none, #660). The Pass/Fail
   * radios are hidden (there is nothing to mark) and a "Not applicable" note
   * replaces them, so the agent is not asked to answer an unanswerable
   * question. The completion gate excludes it (see `questionNotApplicable`).
   */
  notApplicable?: boolean;
  /**
   * When true the authoritative source for this question could NOT be checked
   * (the read failed / was unavailable — e.g. EDS High joint accounts when the
   * `customer/joint-associations` fetch errored, #660). Distinct from
   * `notApplicable`: the Pass/Fail radios STILL show (the agent can record an
   * explicit Fail), but an explicit "could not check" note is surfaced so the
   * absent value is understood as an error state, not an authoritative empty —
   * and completion stays blocked (Pass can't be satisfied with no on-file
   * value). Fail-closed by construction (ai-review HIGH, #660).
   */
  unavailable?: boolean;
  /**
   * Grey informational note rendered IN PLACE OF the bold on-file value when
   * the question has no value to display but is still answerable — e.g. the
   * joint-accounts question when the authoritative read genuinely returned no
   * joint accounts ("No joint accounts on file.", #817). Unlike
   * `notApplicable`, the Pass/Fail radios STILL show: @dylan219's #817 ruling
   * is that the agent must always mark Pass/Fail for this question even when
   * there are none (superseding the #660 N/A treatment).
   */
  infoNote?: string;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-4">
      <div className="flex flex-col">
        <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
          {label}
        </h3>
        {hint && (
          <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0 mb-1 whitespace-pre-line">
            {hint}
          </p>
        )}
        {checkAction && (
          <div className="mb-2">
            <Button
              type="button"
              onClick={checkAction.onClick}
              aria-label={checkAction.ariaLabel}
              className="bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-md px-4 py-2 h-auto font-['Arial',sans-serif] font-bold text-[14px]"
            >
              {checkAction.label}
            </Button>
          </div>
        )}
        {listValue && listValue.length > 0 ? (
          <ul className="list-none m-0 p-0 space-y-1">
            {listValue.map((entry) => (
              <li
                key={entry}
                className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
              >
                {entry}
              </li>
            ))}
          </ul>
        ) : (
          value !== undefined && (
            <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
              {value}
            </p>
          )
        )}
        {infoNote && (
          <p
            role="note"
            className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0 mt-1"
          >
            {infoNote}
          </p>
        )}
        {unavailable && (
          <p
            role="status"
            className="text-[14px] font-['Arial',sans-serif] text-[#b00020] m-0 mt-1"
          >
            Could not check joint accounts — mark Fail if it cannot be
            confirmed.
          </p>
        )}
      </div>
      <div className="ml-auto mt-5">
        {notApplicable ? (
          <p className="text-[14px] font-['Arial',sans-serif] italic text-[#6b7280] m-0">
            Not applicable
          </p>
        ) : (
          <PassFailRadios name={name} value={status} onChange={onChange} />
        )}
      </div>
    </div>
  );
}

interface OtpGenerateResult {
  sent: boolean;
  resultCode?: string;
  maskedMobile?: string;
  attemptsRemaining?: number;
  blocked?: boolean;
  message?: string;
}

interface OtpValidateResult {
  valid: boolean;
  resultCode?: string;
  message?: string;
  attemptsRemaining?: number;
  expired?: boolean;
  blocked?: boolean;
}

/** Maximum OTP validation attempts within the 5-minute window (CCF-16). */
const MAX_OTP_ATTEMPTS = 3;

/**
 * The skip-2FA reason list lives in `../lib/skip-2fa-reasons.js` (extracted for
 * #534 so the registration screens reuse the SAME "reason for non-completion"
 * field). The skip is offered on HIGH-security variants here (the #287 ruling
 * forbids it on medium screens, ECL medium being a separate follow-up
 * increment).
 */

/**
 * The shape of the per-customer identity-question data returned by
 * `POST /api/security/questions` (the bank-gateway PascalCase payload). All
 * fields optional — present per the customer's segment/variant.
 */
interface SecurityQuestionsData {
  CustomerName?: string;
  Dob?: string;
  MothersMaidenName?: string;
  MobileNumber?: string;
  Landline?: string;
  RecentTransaction?: string;
  RecurringPayment?: string;
  JointAccounts?: string[];
  VisaCreditLimit?: string;
  VisaCardholders?: string[];
  VisaCardExpiry?: string;
  PropertyAddress?: string;
  MortgageDrawdownYear?: string;
  MortgageTerm?: string;
  MortgageBrokerOrBranch?: string;
  HomeInsuranceProvider?: string;
  LifeAssuranceProvider?: string;
  HasDirectDebit?: string;
  DirectDebitBank?: string;
  MortgageRepaymentProtection?: string;
  FirstSchool?: string;
  FavouriteMusic?: string;
  EyeColour?: string;
  /**
   * The BUS24 "3/2 additional security questions" (first school attended /
   * favourite music / eye colour, CCF-23/42) as FREE TEXT — issue #334,
   * mirroring the registration screens' identical ruling (issues #305/#311):
   * the real gateway carries these on `customer/info`'s single
   * `SecurityQuestions` field with no formal schema or guaranteed order (an
   * agent free-types into it elsewhere), so the BFF comma-splits it and
   * returns the list as-is (`getSecurityQuestions`). `FirstSchool`/
   * `FavouriteMusic`/`EyeColour` above remain the MOCK-ONLY convenience
   * fields with no real-gateway equivalent (`customer/security-questions`
   * 500s on the real gateway — issue #76) — when neither of those is
   * populated but this list is, the `knowledgeGroup` falls back to the same
   * "enable together / display verbatim, never positionally matched"
   * treatment `registration-common.tsx` already uses for the identical data.
   */
  AdditionalQuestionAnswers?: string[];
}

/**
 * Resolves the displayed value for one identity question from the customer
 * context and the fetched security-questions data. Returns the value to show
 * (or "Not available"), plus the system the agent verifies against (CCF maps
 * VISA → TSYS, mortgage → Hogan).
 */
type QuestionResolver = (args: {
  customerName: string;
  dob: string;
  q: SecurityQuestionsData;
}) => string;

/**
 * Resolves the raw multi-entry answer (e.g. joint accounts, VISA
 * cardholders) so it can be rendered as a discrete list rather than a single
 * `; `-joined string (issue #291 / CCF-26). Returns `undefined` when the
 * underlying field isn't a list, or an empty array when it is but has no
 * entries.
 */
type QuestionListResolver = (args: {
  customerName: string;
  dob: string;
  q: SecurityQuestionsData;
}) => string[] | undefined;

/** A single identity question rendered by the security screen. */
export interface SecurityQuestion {
  /** Stable key used for the radio `name` and React key. */
  readonly key: string;
  /** The label the agent reads. */
  readonly label: string;
  /** Resolves the answer value to display. */
  readonly resolve: QuestionResolver;
  /**
   * Resolves the same answer as a list of entries when the underlying field
   * is multi-valued (JointAccounts, VisaCardholders) — see `IdentityQuestion`.
   */
  readonly resolveList?: QuestionListResolver;
  /**
   * When set, this question supports a "not on file" branch (CCF-5/26/42):
   * if the resolved value is absent the agent ticks "<noun> not on file",
   * which reveals the fallback (other products, or landline) and excludes the
   * field from the mandatory Pass/Fail.
   */
  readonly notOnFile?: NotOnFileBranch;
  /**
   * Grey sub-instruction shown beneath the label (e.g. "Check TSYS for credit
   * card limit" on the VISA external checks — CCF-5). Informational only.
   */
  readonly hint?: string;
  /**
   * When true this question is verified by the agent against an EXTERNAL system
   * (TSYS, for the VISA high-security credit-limit / cardholder / recent-
   * transaction checks — CCF-5, issue #223, mirroring the VISA registration fix
   * for #48). The screen shows only the question + `hint` and the Pass/Fail
   * radios, never a value (even when our data happens to carry one — the Figma
   * shows no value for a TSYS check). Such a question is exempt from the
   * on-file-value fail-closed rule the identity questions obey, but still
   * requires an explicit Pass to contribute to a passing outcome.
   */
  readonly externalCheck?: boolean;
  /**
   * An in-app action the agent can trigger to verify this question (distinct
   * from `externalCheck`, which points the agent at a swivel-chair system).
   * E.g. the EDS High recurring-payment question offers "Check Transactions"
   * (CCF-47) so the agent can look up the account's transaction history —
   * CCF-26, issue #307. Navigates in the SAME tab (issue #344 — @dylan219's
   * ruling superseding the earlier new-tab design): `SecurityScreen` overrides
   * `onClick` at render time (see `handleCheckAction`) to snapshot the
   * in-progress check state first, then navigate to `/transactions`; the
   * Transactions screen shows a "Back to High Security" button that returns
   * the agent here with that state restored. The `onClick` below is therefore
   * an inert placeholder — it is never invoked directly, only the type needs
   * satisfying.
   */
  readonly checkAction?: {
    label: string;
    ariaLabel: string;
    onClick: () => void;
  };
  /**
   * Opt this question OUT of the on-file-value fail-closed rule so it passes on
   * an explicit agent Pass mark alone — because the agent verifies it via its
   * in-app `checkAction` (e.g. EDS High recurring-payment "Check Transactions",
   * CCF-47/CCF-26) rather than by comparing a displayed on-file string, and the
   * backing field is frequently absent on the real gateway (#282/#360). This is
   * a DELIBERATE per-question exemption — it is intentionally NOT inferred from
   * the presence of a `checkAction`, so a future check-action question that must
   * stay value-backed does not silently fail open (ai-review #360).
   */
  readonly agentVerified?: boolean;
  /**
   * Treat this question as NOT APPLICABLE (auto-satisfied, excluded from the
   * mandatory Pass/Fail completion gate) when it has NO on-file value AND no
   * other way to answer it — issue #660. The EDS High "Have you any joint
   * accounts held in PTSB?" question (CCF-26) is authoritatively sourced from
   * `customer/joint-associations` (#281/#282); a customer with NO joint
   * accounts is the EXPECTED real-gateway rendering ("Not available"), not a
   * data defect. Such a question is genuinely UNANSWERABLE — the agent has no
   * Pass/Fail to record against a non-existent joint account — so requiring it
   * would block completion forever ("Answer every security question (Pass or
   * Fail)") after all answerable questions and 2FA are done.
   *
   * The exemption is narrow and fail-CLOSED by construction: it only applies
   * when the resolved value is absent AND the question offers no `notOnFile`
   * branch, `externalCheck`, or `agentVerified` path (see
   * `questionNotApplicable`). When a joint-account value IS on file, the
   * question stays fully mandatory and the agent must mark it Pass/Fail as
   * before. It is a DELIBERATE per-question opt-in — never inferred — so no
   * other identity question (name/DoB/…) can silently fail open if its value
   * were ever absent.
   */
  readonly optionalWhenAbsent?: boolean;
}

/** The kind of "not on file" fallback a question offers (CCF-5/26/42). */
type NotOnFileBranch = 'mothersMaidenName' | 'mobile';

/**
 * A grouped "confirm any 1 of N" knowledge-based question block (CCF-23/42).
 * The agent selects WHICH of the listed questions they confirmed and marks the
 * group Pass or Fail with ONE control — not N individually-mandatory rows.
 */
export interface KnowledgeGroupConfig {
  /** Heading shown on the dark tab above the box (faithful to the Figma). */
  readonly heading: string;
  /** The questions; the agent picks one to confirm. */
  readonly questions: readonly SecurityQuestion[];
  /**
   * BUS24 Medium/High (SCRN-05/10, CCF-23/42) only — issue #334, mirroring
   * issue #305/#311's ruling for the identical registration-screen data: the
   * on-file answers are free text from `customer/info` with NO guaranteed
   * order or 1:1 mapping to the named questions above (an agent free-types
   * into the source field elsewhere). When set, and NONE of this group's
   * named questions has its own on-file value (the real-gateway shape), the
   * screen falls back to showing `AdditionalQuestionAnswers` once, verbatim,
   * as a plain list, and enables every radio in the group together (any one
   * on-file answer is enough for "any 1 of N") — instead of gating each named
   * question individually on `FirstSchool`/`FavouriteMusic`/`EyeColour`,
   * which the real gateway never populates. Mock-only customers that DO carry
   * the named fields keep the original per-question behaviour unchanged.
   */
  readonly additionalAnswersUnordered?: boolean;
  /**
   * When true, all questions are asked (not "select 1 of N") — each gets its
   * own Pass/Fail radio, and the group is OK only when all are marked Pass.
   * The dark heading bar and "confirm any one…" instruction are suppressed.
   * CCF-42 (#722): Business High Security AC change.
   */
  readonly askAll?: boolean;
}

/**
 * A "choose any N of M" question group (CCF-24 mortgage). The agent selects N
 * of the listed questions and marks each chosen one Pass/Fail.
 */
export interface ChooseGroupConfig {
  /** Heading shown on the dark tab above the box. */
  readonly heading: string;
  /** How many of the questions the agent must choose + confirm. */
  readonly chooseCount: number;
  /** The selectable questions. */
  readonly questions: readonly SecurityQuestion[];
}

/** Per-variant configuration for the shared security screen (SCRN 4..10). */
export interface SecurityVariantConfig {
  /** RBAC screen id, e.g. "SCRN-04". */
  readonly screenId: string;
  /** Heading shown at the top (faithful to the Figma). */
  readonly heading: string;
  /** The ordered, always-mandatory identity questions for this variant. */
  readonly questions: readonly SecurityQuestion[];
  /**
   * #427 — the human-readable name of the security FLOW this variant belongs to
   * (e.g. "EDS", "Visa", "Business", "Mortgage"), stamped into the customer
   * context on completion so the banner can name which flow verified the
   * customer ("EDS Medium Security Verified"). Optional; absent ⇒ the banner
   * shows the plain "<Level> Security Verified".
   */
  readonly verificationLabel?: string;
  /**
   * The verification level GRANTED on successful completion (CCF-53). Medium
   * variants (SCRN-04/05) grant `'medium'`; high variants grant `'high'`. The
   * shared screen previously hard-coded `'high'`, over-granting the medium
   * VISA flow.
   */
  readonly grantedLevel: 'medium' | 'high';
  /** Whether the mandatory 2FA OTP step applies (CCF-16). */
  readonly requires2fa: boolean;
  /**
   * Optional grouped "confirm any 1 of N" knowledge-based block (CCF-23/42).
   */
  readonly knowledgeGroup?: KnowledgeGroupConfig;
  /** Optional "choose any N of M" block (CCF-24 mortgage). */
  readonly chooseGroup?: ChooseGroupConfig;
  /**
   * Note shown under the heading — e.g. "Please check TSYS" (VISA) or
   * "Please check Hogan" (mortgage). Optional.
   */
  readonly sourceNote?: string;
  /**
   * Optional dark section-heading bar shown immediately above the mandatory
   * identity questions — e.g. "Mandatory selection" on the Mortgage Security
   * screen (SCRN-09 / CCF-24), matching the approved Figma. Optional.
   */
  readonly mandatoryHeading?: string;
  /**
   * When set, the agent first captures a card number (the label is shown next to
   * the field) before the identity questions — e.g. the 16-digit Visa Credit
   * Card number for the Authorised VISA User flow (CCF-25). Optional.
   */
  readonly cardNumberEntry?: string;
  /**
   * When true, this is a MEDIUM security page that participates in the security
   * cross-navigation dropdown (issue #101): it offers the OTHER medium security
   * pages plus its OWN relevant (same-family) high security page. The page must
   * be registered in `MEDIUM_SECURITY_PAGES` (keyed by `screenId`). High /
   * authorised-user variants leave this unset (no medium menu — see `highNav`).
   */
  readonly mediumNav?: boolean;
  /**
   * When true, this is a HIGH security page that surfaces the cross-navigation
   * dropdown offering ALL the medium security options (issue #117 / #101). The
   * high variants (SCRN-06/07/08/09/10) set this; medium variants use
   * `mediumNav` instead.
   */
  readonly highNav?: boolean;
  /**
   * Where to navigate on a SUCCESSFUL pass. Defaults to the customer dashboard
   * (`/dashboard`). The Broker 24 medium security variant (CCF-318 / #425) sets
   * this to `/reset-internet-password` (SCRN-57) instead: a broker log-in is
   * not linked to accounts, so the dashboard would be blank — the agent is
   * routed to the Reset Internet Password screen once broker security passes.
   */
  readonly successRoute?: string;
}

/** Formats an ISO `YYYY-MM-DD` date of birth as `DD/MM/YYYY` for display. */
function formatDob(dob: string | undefined): string {
  if (!dob) return 'Not available';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dob;
}

// --- Reusable question resolvers -----------------------------------------

/** Full name — always from the identified customer context. */
export const qFullName: SecurityQuestion = {
  key: 'full-name',
  label: 'Full name:',
  resolve: ({ customerName }) => customerName || '—',
};

/** Date of birth — formatted DD/MM/YYYY. */
export const qDob: SecurityQuestion = {
  key: 'dob',
  label: 'Date of birth:',
  resolve: ({ dob }) => formatDob(dob),
};

/** Builds a question that reads a single field from the questions payload. */
function fieldQuestion(
  key: string,
  label: string,
  pick: (q: SecurityQuestionsData) => string | string[] | undefined,
  extra: Partial<Omit<SecurityQuestion, 'key' | 'label' | 'resolve'>> = {},
): SecurityQuestion {
  return {
    key,
    label,
    resolve: ({ q }) => {
      const v = pick(q);
      if (Array.isArray(v)) return v.length ? v.join('; ') : 'Not available';
      return v ?? 'Not available';
    },
    resolveList: ({ q }) => {
      const v = pick(q);
      return Array.isArray(v) ? v : undefined;
    },
    ...extra,
  };
}

export const qMothersMaidenName = fieldQuestion(
  'mothers-maiden-name',
  'Mother’s maiden name:',
  (q) => q.MothersMaidenName,
  { notOnFile: 'mothersMaidenName' },
);
export const qMobileNumber = fieldQuestion(
  'mobile-number',
  'Mobile number:',
  (q) => q.MobileNumber,
  { notOnFile: 'mobile' },
);
export const qRecurringPayment = fieldQuestion(
  'recurring-payment',
  'Do you have any recurring payments that go out of your account each month?',
  (q) => q.RecurringPayment,
  {
    // Figma "additional scripting" for the recurring-payment question (CCF-26
    // screen 7): the agent's script for the Yes/No branches, plus a "Check
    // Transactions" button (CCF-47) to look up the account's transaction
    // history before marking Pass/Fail — issue #307.
    hint: 'If Yes – check one payment name and how often it goes out\nIf No – check account history for last two months',
    // Agent-verified via the "Check Transactions" action below rather than by a
    // compared on-file value (CCF-47/CCF-26); `RecurringPayment` is frequently
    // absent on the real gateway (#282), so this question opts out of the
    // on-file-value fail-closed rule — an explicit, deliberate exemption (#360).
    agentVerified: true,
    checkAction: {
      label: 'Check Transactions',
      ariaLabel: 'Check customer transactions',
      // Issue #344 (@dylan219): navigates the agent to the Transactions screen
      // in the SAME tab, with a "Back to High Security" button there to return
      // and continue the check. `SecurityScreen` overrides this `onClick` at
      // render time (`handleCheckAction`) so it can snapshot the in-progress
      // check state first — this placeholder is never invoked directly.
      // Supersedes the earlier #325 new-tab design: that opened WITHOUT
      // `noopener` so the new tab could inherit/clone this tab's sessionStorage
      // (the identified-customer context) via same-origin auxiliary-browsing-
      // context cloning. Same-tab navigation shares the SAME sessionStorage
      // instance directly, so that cloning trick is now moot for this call
      // site (the `noopener` no-noopener tradeoff no longer applies here —
      // `MobileUpdateScreen.tsx` still opens a genuine new tab and keeps its
      // own no-noopener precedent unchanged).
      onClick: () => {},
    },
  },
);
export const qRecentTransaction = fieldQuestion(
  'recent-transaction',
  'Can you confirm a recent transaction on the account?',
  (q) => q.RecentTransaction,
  { externalCheck: true, hint: 'Check TSYS for recent transaction' },
);
export const qJointAccounts = fieldQuestion(
  'joint-accounts',
  'Have you any joint accounts held in PTSB?',
  (q) => q.JointAccounts,
  {
    hint: 'If yes, please confirm account holder’s name',
    // #817 (@dylan219, superseding #660's N/A treatment): a customer with NO
    // joint accounts is now ANSWERABLE, not Not-Applicable — the screen shows
    // "No joint accounts on file." and the agent must still mark Pass/Fail
    // (confirming verbally). `optionalWhenAbsent` now means "when the
    // authoritative `customer/joint-associations` read genuinely resolved to
    // empty, an explicit Pass alone satisfies it (no on-file value to
    // compare)" — see `questionPasses`. An errored/not-yet-loaded read still
    // fails closed there (#660 invariant preserved).
    optionalWhenAbsent: true,
  },
);
export const qVisaCreditLimit = fieldQuestion(
  'visa-credit-limit',
  'Credit limit on Visa credit card',
  (q) => q.VisaCreditLimit,
  { externalCheck: true, hint: 'Check TSYS for credit card limit' },
);
export const qVisaCardholders = fieldQuestion(
  'visa-cardholders',
  'Any other card holders on the account?',
  (q) => q.VisaCardholders,
  { externalCheck: true, hint: 'Check TSYS for authorised user name' },
);
export const qVisaCardExpiry = fieldQuestion(
  'visa-card-expiry',
  'Confirm the expiry date on the Visa credit card (check TSYS):',
  (q) => q.VisaCardExpiry,
  { externalCheck: true, hint: 'Check TSYS for card expiry date' },
);
/**
 * Builds a mortgage/Hogan question (CCF-24/CCF-59): "No API integration" per
 * the user story — the agent swivels to the separate Hogan mortgage system to
 * validate the answer, so the screen must never attempt to source/compare an
 * on-file value for these (there is no gateway field to source it from; the
 * `customer/security-questions` endpoint is mock-only and 500s on the real
 * gateway — issue #76). Modelled as `externalCheck` (the same pattern already
 * used for the VISA/TSYS swivel-chair checks, #223): the screen shows only the
 * question + Pass/Fail, never a value and never a "Not available"/"not on
 * file" fallback — the agent records the outcome directly from Hogan (#309).
 */
function hoganQuestion(key: string, label: string): SecurityQuestion {
  return { key, label, resolve: () => '', externalCheck: true };
}

/**
 * Full property / correspondence address (Mortgage Security, SCRN-09/CCF-24).
 *
 * Issue #685 (@dylan219, product override): the Mandatory Section must DISPLAY
 * the customer's actual CIF address returned from the APIs, not the bare static
 * "Full property address or correspondence address (Check Hogan)" prompt. This
 * REVERSES the earlier #309 treatment (which, per CCF-24/CCF-59's "No API
 * integration" note, modelled ALL the mortgage items — including this one — as
 * value-less `hoganQuestion` Pass/Fail rows). Dylan's newer, authoritative
 * instruction supersedes #309 for THIS field only: the on-file address genuinely
 * IS available (the BFF sources it from `customer/identification`'s `Address1..5`
 * lines, the same source already surfaced as `SignatoryAddress`/`BorrowerAddress`
 * for the Broker24/Mortgage REGISTRATION screens), so this becomes a normal
 * `fieldQuestion` that renders `PropertyAddress`. The other 7 "choose 2 of 7"
 * mortgage questions stay Hogan swivel-chair checks (unchanged, #309). When the
 * gateway carries no address the field shows "Not available" (never fabricated).
 */
export const qPropertyAddress = fieldQuestion(
  'property-address',
  'Full property address or correspondence address',
  (q) => q.PropertyAddress,
);
export const qMortgageYear = hoganQuestion(
  'mortgage-year',
  'Can you confirm the year your mortgage was drawn down?',
);
export const qMortgageTerm = hoganQuestion(
  'mortgage-term',
  'Can you confirm the original length of your mortgage term?',
);
export const qMortgageBrokerOrBranch = hoganQuestion(
  'mortgage-broker-branch',
  'Can you confirm if you took your mortgage out with a branch or a broker?',
);
export const qHomeInsurance = hoganQuestion(
  'home-insurance',
  'Can you confirm the company of your home insurance provider?',
);
export const qLifeAssurance = hoganQuestion(
  'life-assurance',
  'Can you confirm your life assurance provider?',
);
/**
 * Direct-Debit bank (CCF-24): one freely-selectable Hogan question, exactly as
 * the approved Figma shows it. Issue #580 (@dylan219, 2026-08-07 — "yes remove
 * the checkbox and dependency as requested"): the extra "Customer has a Direct
 * Debit set up" confirmation checkbox + dependency message added by #309 are
 * removed so the item matches the Figma as a single selectable question. The
 * "Only valid question if customer has direct debit" caveat stays inline in the
 * label, so eligibility is the agent's judgement call (asking the customer /
 * checking Hogan) — there is no reliable on-file DD flag to gate it on.
 */
export const qDirectDebitBank = hoganQuestion(
  'direct-debit-bank',
  'Can you confirm the bank that your direct debit is deducted from? (Only valid question if customer has direct debit)',
);
export const qMortgageRepaymentProtection = hoganQuestion(
  'mortgage-repayment-protection',
  'Do you pay a mortgage repayment protection policy with your mortgage?',
);
export const qFirstSchool = fieldQuestion(
  'first-school',
  'Can you confirm the first school you attended?',
  (q) => q.FirstSchool,
);
export const qFavouriteMusic = fieldQuestion(
  'favourite-music',
  'Can you confirm your favourite type of music?',
  (q) => q.FavouriteMusic,
);
export const qEyeColour = fieldQuestion(
  'eye-colour',
  'Can you confirm your eye colour?',
  (q) => q.EyeColour,
);

/**
 * #637 (CCF-113 / SCRN-56) — maps a question's stable `key` to its content-
 * catalogue key so the wording the agent reads is admin-editable. **Text only,
 * never logic:** only the displayed LABEL is overridable; question selection,
 * eligibility, Pass/Fail, `resolve` value-sourcing and the `externalCheck`
 * swivel logic all key off the (unchanged) question object, never this string.
 * A question absent from this map keeps its baked-in `.label` verbatim. The
 * catalogue value ITSELF defaults to the same baked-in wording (server + SPA),
 * so with no admin override the screen renders exactly as before.
 */
export const QUESTION_LABEL_CONTENT_KEY: Readonly<Record<string, string>> = {
  'full-name': 'security.question.full_name',
  dob: 'security.question.dob',
  'mothers-maiden-name': 'security.question.mothers_maiden_name',
  'mobile-number': 'security.question.mobile_number',
  'recurring-payment': 'security.question.recurring_payment',
  'recent-transaction': 'security.question.recent_transaction',
  'joint-accounts': 'security.question.joint_accounts',
  'visa-credit-limit': 'security.question.visa_credit_limit',
  'visa-cardholders': 'security.question.visa_cardholders',
  'visa-card-expiry': 'security.question.visa_card_expiry',
  'property-address': 'security.question.property_address',
  'mortgage-year': 'security.question.mortgage_year',
  'mortgage-term': 'security.question.mortgage_term',
  'mortgage-broker-branch': 'security.question.mortgage_broker_branch',
  'home-insurance': 'security.question.home_insurance',
  'life-assurance': 'security.question.life_assurance',
  'direct-debit-bank': 'security.question.direct_debit_bank',
  'mortgage-repayment-protection':
    'security.question.mortgage_repayment_protection',
  'first-school': 'security.question.first_school',
  'favourite-music': 'security.question.favourite_music',
  'eye-colour': 'security.question.eye_colour',
};

/** One product/account row from `/security/products-summary` (issues #346/#347/#604). */
interface ProductSummaryRow {
  // Always present — a row (account or product) is only emitted when its
  // masked id carries at least 4 trailing digits (issue #604).
  AccountLast4: string;
  AccountType?: string;
  Nickname?: string;
  // Present only for a non-account product row (Visa/mortgage/insurance/
  // investment — issue #604).
  ProductDescription?: string;
}

/**
 * Formats one of the customer's ACTUAL other products/accounts for the
 * MMN-not-on-file fallback list (issue #347) — the nickname when set,
 * otherwise the account type, plus the last 4 digits (never the full
 * number), mirroring the joint-accounts confirmation string's style.
 */
function formatProductEntry(p: ProductSummaryRow): string {
  // A non-account product row (Visa credit card, mortgage, insurance,
  // investment — issue #604) carries `ProductDescription`; an account row uses
  // the nickname/type. Every row has a last-4 suffix (rows without trailing
  // digits are dropped server-side), so it is always appended.
  const desc =
    p.Nickname?.trim() ||
    p.ProductDescription?.trim() ||
    p.AccountType?.trim() ||
    'Account';
  return `${desc} ••${p.AccountLast4}`;
}

/**
 * Fetch/render state for the customer's ACTUAL other products/accounts (issue
 * #347). `'idle'` = not yet asked (the "not on file" tick has not been ticked);
 * `'loading'` = the authoritative fetch is in flight; `'error'` = the fetch
 * failed or the BFF returned non-OK — deliberately DISTINCT from an
 * authoritative empty list, so the screen never silently degrades to a weaker
 * fallback script on an infra/gateway error (ai-review HIGH on the sibling
 * #346 change); an array (possibly empty) = the authoritative result loaded.
 */
type ProductsSummaryState = 'idle' | 'loading' | 'error' | ProductSummaryRow[];

/**
 * Fetch/render OUTCOME of the authoritative `customer/joint-associations` read
 * (EDS High joint-accounts question, CCF-26 / #660). Tracked separately from
 * the merged `questionData.JointAccounts` value because a cleared/absent value
 * is ambiguous — it is how BOTH a genuinely-empty success and a failed / not-
 * yet-loaded fetch render ("Not available"). `'idle'` = not yet fetched;
 * `'loading'` = the fetch is in flight; `'error'` = the fetch failed or the BFF
 * returned non-OK; `'empty'` = the endpoint SUCCEEDED and the customer has NO
 * joint accounts (the only state that makes the question NOT APPLICABLE);
 * `'loaded'` = the endpoint succeeded with one or more joint accounts (the
 * question is then fully answerable/mandatory). Only `'empty'` may fail open to
 * N/A — every other state FAILS CLOSED (ai-review HIGH, #660).
 */
type JointAssociationsState = 'idle' | 'loading' | 'error' | 'empty' | 'loaded';

/**
 * The run key that binds a screen's local verification state to the customer +
 * variant it was produced for. Used to guarantee a verification can never be
 * applied to a different customer than the one it was performed against (the
 * customer-swap fail-closed guard).
 */
export function securityRunKey(
  customerNumber: string,
  screenId: string,
): string {
  return `${customerNumber}|${screenId}`;
}

/**
 * Whether the verification state stamped with `loadedRunKey` belongs to the
 * customer + variant currently in context. Returns false when no run has loaded
 * (`null`) or when the identity has changed — so a stale-but-passing state after
 * a customer swap cannot satisfy completion. This is the explicit, timing-
 * independent guard that does NOT rely on the passive reset effect having run.
 */
export function verificationStateMatchesCustomer(
  loadedRunKey: string | null,
  customerNumber: string,
  screenId: string,
): boolean {
  return (
    loadedRunKey !== null &&
    loadedRunKey === securityRunKey(customerNumber, screenId)
  );
}

/**
 * sessionStorage key for the in-progress security-check snapshot (issue #344).
 * "Check Transactions" (the EDS High recurring-payment question, CCF-47)
 * navigates the agent to the Transactions screen in the SAME tab, unmounting
 * `SecurityScreen` and losing its local `useState`. This snapshot preserves the
 * agent's Pass/Fail selections, "not on file" ticks, knowledge/choose-group
 * progress and 2FA/OTP progress across that round trip, so returning via the
 * Transactions screen's "Back to High Security" button resumes exactly where
 * the agent left off. Bound to the SAME `securityRunKey` (customer + screen id)
 * as `loadedRunKey` — reusing the existing fail-closed pattern rather than a
 * parallel key scheme — so a snapshot can never be applied to the wrong
 * customer or variant.
 *
 * The key itself lives in `../security-run-snapshot.js` (imported above) so
 * App's Clear Session (#780) shares the exact literal and the two can never
 * drift apart.
 */

interface SecurityRunSnapshot {
  runKey: string;
  /**
   * The live Genesys conversation id (`customer.contextConversationId`) the run
   * belongs to (#412 increment 2). Binds a persisted in-progress run to the
   * SAME call, so a return to High Security within that call pre-populates the
   * previously-answered questions + keeps 2FA marked complete, while a fresh /
   * different call (a new conversation id) — or a session with none — never
   * inherits it (fail-closed). Server-resolved from the Genesys interaction
   * (#357/#201), not a client toggle; may be `undefined` for the pre-#412
   * Check-Transactions round-trip snapshot, which matches on `runKey` alone.
   */
  conversationId?: string;
  statuses: Record<string, CheckStatus>;
  notOnFile: Record<NotOnFileBranch, boolean>;
  kbaSelected: string;
  kbaStatus: CheckStatus;
  chosen: string[];
  skip2faReason: string;
  // NOTE (#412): the card/PAN value is deliberately NOT part of the persisted
  // snapshot. Pre-population on a return to High Security only needs the
  // previously-answered questions + the 2FA-complete state; persisting the full
  // 16-digit Visa card number (Authorised VISA User, CCF-25) to sessionStorage
  // would broaden storage of a PAN (a PCI concern), so it is excluded — the
  // agent re-enters the card number if the card-entry step is revisited.
  otpStatus: CheckStatus;
  otpBlocked: boolean;
  otpSent: boolean;
  maskedMobile: string;
  otpAttemptsRemaining: number;
}

/**
 * Loads the in-progress snapshot, only when it belongs to the SAME customer +
 * variant (`runKey`) — a snapshot for a different run is stale and ignored
 * (fail-closed, mirrors `verificationStateMatchesCustomer`).
 *
 * `conversationId` (#412 increment 2) additionally binds the restore to the
 * SAME live call: when supplied it must match the snapshot's own
 * `conversationId`, so a return to High Security within the current online call
 * pre-populates while a fresh / different call — or one without a conversation
 * id — never does. It is left unset by the pre-#412 Check-Transactions
 * round-trip restore, which is a same-tab, within-run navigation and matches on
 * `runKey` alone.
 */
function loadSecurityRunSnapshot(
  runKey: string,
  conversationId?: string,
): SecurityRunSnapshot | null {
  try {
    const raw = sessionStorage.getItem(SECURITY_RUN_SNAPSHOT_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as SecurityRunSnapshot;
    if (snap.runKey !== runKey) return null;
    // When the caller binds to a specific call, the snapshot must belong to the
    // SAME call (fail-closed: a snapshot for another / no conversation is
    // ignored). When no conversation id is required (the Check-Transactions
    // round trip), runKey alone is sufficient.
    if (
      conversationId !== undefined &&
      snap.conversationId !== conversationId
    ) {
      return null;
    }
    return snap;
  } catch {
    return null;
  }
}

/**
 * The full set of identity-question keys a variant renders — the mandatory
 * questions plus any grouped knowledge / choose-N questions. Used to filter a
 * carried-over medium run's answers down to the questions this HIGH variant
 * actually shows (issue #456): only overlapping questions are pre-populated,
 * never a stale answer for a question that isn't on this page.
 */
function variantQuestionKeys(config: SecurityVariantConfig): Set<string> {
  const keys = new Set<string>();
  for (const q of config.questions) keys.add(q.key);
  for (const q of config.knowledgeGroup?.questions ?? []) keys.add(q.key);
  for (const q of config.chooseGroup?.questions ?? []) keys.add(q.key);
  return keys;
}

/**
 * Loads a snapshot from a DIFFERENT security variant performed earlier on the
 * SAME live call, for the medium→high crossover (issue #456 — @dylan219's #412
 * ruling applied to the medium→high transition). When an agent routes to a HIGH
 * security page after answering questions / completing 2FA on a MEDIUM page in
 * the same call, the previously-answered questions must be pre-populated and 2FA
 * marked already-complete — the agent must not redo work already done on the
 * live call.
 *
 * Fail-closed like {@link loadSecurityRunSnapshot}:
 *   - a conversation id is REQUIRED (an online journey) — never carry over
 *     without one;
 *   - the snapshot must belong to the SAME customer + SAME call
 *     (`conversationId`); a different / absent call never inherits it;
 *   - the snapshot must be from a DIFFERENT screen (`screenId`) — the
 *     same-screen resume is handled by {@link loadSecurityRunSnapshot}.
 *
 * The returned snapshot is FILTERED to this HIGH variant: `statuses` are
 * restricted to questions the target actually renders (`variantQuestionKeys`),
 * and `notOnFile` branches likewise. The 2FA-complete state (a passed OTP /
 * masked mobile / attempts) is carried verbatim so 2FA stays marked done.
 *
 * This is purely a client-side UX pre-fill; the verification boundary stays
 * server-side (`/security/high/complete` still requires a server-satisfied 2FA
 * step). It is offered only when the customer already reached medium on the
 * call (`carryFrom` a completed/in-progress medium run) — re-showing a passed
 * OTP in the form can never, by itself, elevate the session.
 */
function loadCrossVariantSecurityRunSnapshot(
  config: SecurityVariantConfig,
  customerNumber: string,
  conversationId: string,
): SecurityRunSnapshot | null {
  try {
    const raw = sessionStorage.getItem(SECURITY_RUN_SNAPSHOT_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as SecurityRunSnapshot;
    // Must be bound to THIS call, or we never inherit it (fail-closed).
    if (!snap.conversationId || snap.conversationId !== conversationId) {
      return null;
    }
    // The snapshot's runKey is `${customerNumber}|${screenId}`. It must be the
    // same customer, and a DIFFERENT screen (a prior variant on this call — the
    // same-screen resume is `loadSecurityRunSnapshot`).
    const thisScreenRunKey = securityRunKey(customerNumber, config.screenId);
    if (snap.runKey === thisScreenRunKey) return null;
    if (!snap.runKey.startsWith(`${customerNumber}|`)) return null;

    // Filter the answered questions + not-on-file branches down to the ones
    // this HIGH variant actually renders — never pre-populate a question that
    // isn't on this page.
    const keys = variantQuestionKeys(config);
    const statuses: Record<string, CheckStatus> = {};
    for (const [key, status] of Object.entries(snap.statuses)) {
      if (keys.has(key)) statuses[key] = status;
    }

    return {
      ...snap,
      // Re-key to THIS variant so the restored state binds to this screen (the
      // continuous save effect + completion guard use this screen's runKey).
      runKey: thisScreenRunKey,
      statuses,
    };
  } catch {
    return null;
  }
}

function saveSecurityRunSnapshot(snapshot: SecurityRunSnapshot): void {
  try {
    sessionStorage.setItem(SECURITY_RUN_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // Non-fatal: storage may be unavailable (private mode) — the round trip
    // still works, the agent just resumes with a clean run.
  }
}

function clearSecurityRunSnapshot(): void {
  try {
    sessionStorage.removeItem(SECURITY_RUN_SNAPSHOT_KEY);
  } catch {
    // Non-fatal.
  }
}

/**
 * The HIGH-security screens whose shared "Check …" `checkAction` navigates the
 * agent to Transactions and back (issue #344/#307/#325). Only EDS High
 * currently sets a `checkAction` (the recurring-payment question). Listed here
 * as a small local map — rather than derived from the route registry — to
 * avoid a circular import (`routing/registry.tsx` already imports
 * `security-variants.tsx`, which imports this module).
 */
const CHECK_ACTION_RETURN_ROUTES: Record<string, string> = {
  'SCRN-07': '/security/eds-high',
};

/**
 * Shared, parameterised security screen for the seven Security variants
 * (SCRN 4..10; CCF-53/23/25/26/5/24/42).
 *
 * It is a generalisation of the SCRN-03 Medium Security port: a list of
 * per-variant identity questions (each Pass/Fail), an optional grouped
 * "confirm any 1 of N" knowledge block (CCF-23/42), an optional "choose any N
 * of M" block (CCF-24 mortgage), and the mandatory 2FA OTP step (CCF-16) where
 * the variant requires it.
 *
 * The customer is marked verified only when EVERY mandatory check passes AND
 * (where required) 2FA passes — if ANY answer fails the customer is referred to
 * branch (CCF-22). A failure is TERMINAL for the run (latched) — exactly as the
 * ECL screens do — so flipping a Fail back to Pass cannot rescue it; the agent
 * restarts ID&V. Verification is COMMITTED on the explicit "Continue to
 * dashboard" action, not speculatively, so a momentarily-passing state that the
 * agent then changes is never left stale. Completion flips `securityLevel` to
 * the variant's granted level (CCF-53: medium variants → `'medium'`, high
 * variants → `'high'`), unlocking the Dashboard.
 *
 * The identity-question values are fetched from the Teller-ID-gated BFF
 * (`POST /api/security/questions`, sourced from Unibanks/TSYS/Hogan) using the
 * Teller ID remembered from ID&V; the OTP step reuses the SCRN-03 endpoints.
 */
export function SecurityScreen({
  config,
}: {
  config: SecurityVariantConfig;
}): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, updateCustomer } = useCustomer();

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';

  // #637 (CCF-113 / SCRN-56): the security-failed branch-referral advisory copy
  // is admin-editable via the content catalogue (non-regulatory guidance text).
  // Each resolves to its compiled-in default when there is no override, so the
  // screen renders unchanged. Verification/branch-referral logic is untouched.
  const failHeading = useContent('security.high.fail.heading');
  const failNote = useContent('security.high.fail.note');

  // #637 inc 11 (CCF-113 / SCRN-56): the knowledge-question LABELS are admin-
  // editable. Resolve each question's displayed wording once from the content
  // map (override → baked-in catalogue default), keyed by the question's stable
  // key; anything not mapped, or with no override, renders its compiled-in
  // `.label` verbatim (fail-safe — never blank). Text only: this NEVER affects
  // question selection, Pass/Fail or value-sourcing, which key off the question
  // object itself, not this string.
  const contentMap = useContentMap();
  const labelFor = useMemo(() => {
    return (q: SecurityQuestion): string => {
      const key = QUESTION_LABEL_CONTENT_KEY[q.key];
      return (key ? contentMap[key] : undefined) || q.label;
    };
  }, [contentMap]);

  // The live Genesys conversation id this context belongs to (#357/#201). It
  // is stamped for every online interaction and reset on a NEW call, so it is
  // the authoritative "same call" key — used to bind a persisted in-progress
  // run to the call it was performed on (#412 increment 2). May be undefined on
  // a standalone/offline session that carries no conversation.
  const conversationId = customer.contextConversationId;

  // Restore the in-progress check state at mount in either of two cases:
  //
  //  1. Issue #344 — arriving back via the Transactions screen's "Back to High
  //     Security" button, which sets `state.restoreSecurityRun` (mirrors the
  //     `restore` flag pattern used by `TransactionsScreen.tsx`, issue #55). A
  //     same-tab, within-run round trip: matches on `runKey` alone.
  //
  //  2. #412 increment 2 (@dylan219's 2026-07-17 steer) — a RETURN to High
  //     Security within the SAME online call (e.g. the SCRN-26 Mobile Number
  //     Update High-Security block's "Go to High Security", or any re-entry via
  //     the process menu / navigation). The agent must not have to redo
  //     questions or 2FA they already completed on the live call, so the
  //     previously-answered questions are pre-populated and 2FA stays marked
  //     complete. Bound to the SAME call via `conversationId`: a fresh /
  //     different call (or a session with no conversation id) never inherits a
  //     prior run (fail-closed). This is purely a UX pre-fill — the verification
  //     boundary stays server-side (`/security/high/complete` still requires an
  //     active server-side run whose 2FA step was satisfied), so re-showing a
  //     passed OTP in the form never elevates the session by itself.
  //
  // Any other entry (a fresh navigation on a new call, a direct link with no
  // matching call-bound snapshot) leaves it null, so the screen starts clean.
  // Computed once at mount — read into the `useState` initial values below.
  const restoredSecurity = useRef<SecurityRunSnapshot | null>(
    (() => {
      const runKey = securityRunKey(customerNumber, config.screenId);
      if (
        (location.state as { restoreSecurityRun?: boolean } | null)
          ?.restoreSecurityRun
      ) {
        return loadSecurityRunSnapshot(runKey);
      }
      // A return to High Security within the same call: only when we can bind
      // to a conversation (online journey) — never restore without one.
      if (conversationId) {
        // Same-screen resume (a return to THIS variant) takes precedence.
        const sameScreen = loadSecurityRunSnapshot(runKey, conversationId);
        if (sameScreen) return sameScreen;
        // #456 (medium→high crossover): a HIGH variant carries over a run
        // performed on a DIFFERENT variant earlier on the SAME call — the
        // overlapping answered questions are pre-populated and 2FA stays marked
        // complete. Offered on HIGH variants only (the medium→high transition
        // is the reported case); the medium pages keep a clean start.
        if (config.grantedLevel === 'high') {
          return loadCrossVariantSecurityRunSnapshot(
            config,
            customerNumber,
            conversationId,
          );
        }
      }
      return null;
    })(),
  );

  // A restored snapshot with a passed OTP is a CANDIDATE for the medium→high
  // carryover (#412/#456): the customer completed 2FA earlier on this call. It is
  // NOT sufficient on its own — this is client-only sessionStorage. The server
  // run's 2FA step must be recorded server-side (`/security/high/carry-2fa`)
  // before the "2FA already completed" indicator replaces the OTP entry step, or
  // `/security/high/complete` would still 403 (ai-review #456). Covers BOTH the
  // same-screen resume and the cross-variant crossover — either way a HIGH run's
  // 2FA is only satisfied server-side, never on the restored flag alone.
  // Computed once at mount.
  const twoFaCarryCandidate = useRef<boolean>(
    restoredSecurity.current?.otpStatus === 'pass',
  );

  // Whether the 2FA step has been CARRIED OVER and RECORDED SERVER-SIDE from a
  // same-call OTP (#412/#456) — set only after `/security/high/carry-2fa`
  // succeeds. Drives the "2FA already completed on this call" indicator, which
  // replaces the OTP entry step so the agent does not redo 2FA. Fail-closed:
  // until the server confirms, the OTP/skip controls stay visible.
  const [twoFaCarriedOver, setTwoFaCarriedOver] = useState<boolean>(false);

  // Per-question Pass/Fail state, keyed by question key.
  const [statuses, setStatuses] = useState<Record<string, CheckStatus>>(
    restoredSecurity.current?.statuses ?? {},
  );
  // Optional card-number capture (e.g. Authorised VISA User, CCF-25). The card
  // number / PAN is intentionally NOT restored from the persisted snapshot
  // (#412 — it is never written there; see SecurityRunSnapshot), so a return to
  // High Security always starts this field empty; the agent re-enters it if the
  // card-entry step is revisited.
  const [cardNumber, setCardNumber] = useState('');

  // "Not on file" ticks (CCF-5/26/42), keyed by branch.
  const [notOnFile, setNotOnFile] = useState<Record<NotOnFileBranch, boolean>>(
    restoredSecurity.current?.notOnFile ?? {
      mothersMaidenName: false,
      mobile: false,
    },
  );

  // #346/#347: the customer's ACTUAL other products/accounts for the
  // MMN-not-on-file "Do you have any other products or accounts with PTSB?"
  // fallback — fetched data, not manual agent input, so (like
  // `questionData.JointAccounts`) it is refetched fresh whenever the "not on
  // file" tick is (re-)set rather than carried in the Check-Transactions
  // snapshot. Starts `'idle'` until the tick is set.
  const [otherProducts, setOtherProducts] =
    useState<ProductsSummaryState>('idle');
  // ai-review HIGH (issue #346, 3rd finding): `otherProducts` alone isn't
  // enough to gate answerability — if customerNumber/tellerId/screenId
  // change while a PRIOR resolved array is still in state, there is one
  // render between the props changing and the reset effect firing where a
  // stale (wrong-customer) array would still read as "answerable". This ref
  // records which context the CURRENTLY-held `otherProducts` value actually
  // resolved for; `notOnFileFallbackAvailable` requires an exact context
  // match (not just `Array.isArray`), so that transient render fails closed
  // instead of exposing the previous customer/teller's data.
  const otherProductsContextRef = useRef<{
    customerNumber: string;
    tellerId: string;
    screenId: string;
  } | null>(null);

  // Grouped "confirm any 1 of N" block (CCF-23/42): which question the agent
  // confirmed + the single group Pass/Fail.
  const [kbaSelected, setKbaSelected] = useState<string>(
    restoredSecurity.current?.kbaSelected ?? '',
  );
  const [kbaStatus, setKbaStatus] = useState<CheckStatus>(
    restoredSecurity.current?.kbaStatus ?? null,
  );

  // "Choose any N of M" block (CCF-24 mortgage): the chosen question keys.
  const [chosen, setChosen] = useState<string[]>(
    restoredSecurity.current?.chosen ?? [],
  );
  // Fetched identity-question data (sourced from the gateway).
  const [questionData, setQuestionData] = useState<SecurityQuestionsData>({});
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  // #660 (ai-review HIGH): the OUTCOME of the authoritative
  // `customer/joint-associations` read, tracked separately from the merged
  // `questionData.JointAccounts` value. A cleared/absent `JointAccounts` is
  // ambiguous — it is what BOTH a genuinely-empty success AND a failed /
  // errored / not-yet-loaded fetch look like (each renders "Not available").
  // The joint-accounts question may only be treated as NOT APPLICABLE when the
  // endpoint SUCCEEDED with a genuinely empty list (`'empty'`). On `'idle'` /
  // `'loading'` / `'error'` it must FAIL CLOSED — the question stays
  // un-satisfied and completion blocked — so a transient/failed authoritative
  // check can never let High Security complete.
  const [jointAssociations, setJointAssociations] =
    useState<JointAssociationsState>('idle');
  // The customer/config the CURRENT local verification state was produced for.
  // Bound when the authoritative /security/questions response loads, so the
  // verification can never be committed for a different customer than the one
  // it was actually performed against (see `securityRunKey` / `allChecksPass`).
  // The passive customer-change reset effect runs only AFTER render, so this
  // explicit binding is what guarantees correctness during the transient
  // window where the component has re-rendered with a new customer but the
  // reset has not yet fired.
  const [loadedRunKey, setLoadedRunKey] = useState<string | null>(null);

  const [otpSent, setOtpSent] = useState(
    restoredSecurity.current?.otpSent ?? false,
  );
  const [maskedMobile, setMaskedMobile] = useState(
    restoredSecurity.current?.maskedMobile ?? '',
  );
  const [generating, setGenerating] = useState(false);

  const [otpCode, setOtpCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [otpStatus, setOtpStatus] = useState<CheckStatus>(
    restoredSecurity.current?.otpStatus ?? null,
  );
  const [otpAttemptsRemaining, setOtpAttemptsRemaining] = useState(
    restoredSecurity.current?.otpAttemptsRemaining ?? MAX_OTP_ATTEMPTS,
  );
  const [otpExpired, setOtpExpired] = useState(false);
  const [otpBlocked, setOtpBlocked] = useState(
    restoredSecurity.current?.otpBlocked ?? false,
  );
  // Non-blocking inline warning for a wrong (non-final) OTP code (issue #164,
  // per the @dylan219 UX rule #80/#82/#94): a gateway-calling action's failure
  // must surface the code + message INLINE beside the action button, not only
  // as a transient toast, and stay retryable.
  const [otpError, setOtpError] = useState<{
    code?: string;
    message: string;
    // Which action produced the error, so it renders directly under THAT
    // button (#782): 'generate' → under Send Code; 'validate' → under Confirm.
    source: 'generate' | 'validate';
  } | null>(null);
  // #810: the gateway's (BFF-mapped, ruled #810 copy) message for the expired
  // (2004) state, rendered inline in the dedicated branch alert when present.
  const [otpExpiredMessage, setOtpExpiredMessage] = useState<string | null>(
    null,
  );

  // #287: on HIGH-security screens the agent may skip 2FA by selecting a reason
  // (e.g. no mobile on file). The empty string means "not skipped". Selecting a
  // reason treats the 2FA piece as PASSED (per @dylan219's ruling) — no OTP is
  // generated or verified in this path.
  const [skip2faReason, setSkip2faReason] = useState<string>(
    restoredSecurity.current?.skip2faReason ?? '',
  );

  // #586: when the skip reason is "2FA Exemption Approved by TL" the agent must
  // enter the approving team lead's name, validated the same way as the Mobile
  // Number Update floor-support/team-lead field (Unicode letters, spaces,
  // apostrophes, hyphens). The name is recorded server-side in the audited skip
  // record so the exemption approver is captured.
  const [teamLeadName, setTeamLeadName] = useState('');
  const [teamLeadNameError, setTeamLeadNameError] = useState('');

  // #789: when the skip reason is "2FA Customer Device Issue" the agent must
  // tick a "Troubleshooting completed" checkbox before the skip activates —
  // confirming they completed troubleshooting with the customer. Purely a
  // client-side confirmation gate (no server field); the recorded reason is
  // still the selected reason string.
  const [troubleshootingDone, setTroubleshootingDone] = useState(false);

  // CCF-53: the variant's granted level — already verified at that level only
  // when the persisted context matches AND this is the live result.
  const grantedLevel = config.grantedLevel;

  const resolverArgs = useMemo(
    () => ({
      customerName: customer.name ?? '',
      dob: customer.dob ?? '',
      q: questionData,
    }),
    [customer.name, customer.dob, questionData],
  );

  /** A question is "on file" when its resolved value is a real answer. */
  function hasValue(q: SecurityQuestion): boolean {
    const v = q.resolve(resolverArgs);
    return v !== 'Not available' && v !== '—' && v.trim().length > 0;
  }

  /**
   * Whether a "not on file" tick is a valid branch.
   *
   * #626 (@dylan219, reversing the maiden half of #348): the MAIDEN branch is
   * ALWAYS a valid tick, even when a maiden name IS on file — PTSB branches
   * enter junk ('unknown','TBC','Not known'…) into the maiden field that can't
   * be pattern-matched, so the agent must always be able to record "not on file"
   * and verify against the customer's other products instead. This deliberately
   * re-opens the maiden bypass #348 closed (confirmed acceptable given the junk
   * data); `questionPasses` still requires the agent's explicit Pass on the
   * revealed products fallback, so a tick alone is never sufficient.
   *
   * #662 (CCRS-3447): the MOBILE branch now follows the same rule — the "Mobile
   * number not on file" tick is ALWAYS a valid branch when ticked, even when a
   * mobile IS on file, so the agent can always flag it and verify against the
   * landline instead. This reverses #348 / #626 item 2 (which restricted the
   * mobile tick to a blank field); the no-bypass invariant is preserved because
   * `questionPasses` still requires the agent's explicit Pass on the revealed
   * landline fallback, so a tick alone is never sufficient.
   */
  function notOnFileValid(
    branch: NotOnFileBranch,
    _q: SecurityQuestion,
  ): boolean {
    return notOnFile[branch] && questionsLoaded;
  }

  /**
   * Whether the fallback that a "not on file" branch reveals is itself
   * answerable. The MMN branch reveals the associated-products question
   * (always answerable). The mobile branch reveals the landline question —
   * issue #344 (@dylan219's ruling): even when NO landline is on file either,
   * the agent must still be able to record an explicit Pass/Fail, never an
   * automatic, uncontrollable fail. "No landline on file" stays informational
   * copy only (rendered regardless), it no longer blocks the control. Both
   * branches are therefore always answerable; the no-bypass invariant is
   * preserved because `questionPasses` below still requires the agent's own
   * explicit Pass mark — a "not on file" tick alone is never sufficient.
   * (Previously the mobile branch returned `false` here with no landline,
   * which forced `questionFailed` to latch a fail the agent had no way to
   * avoid — the very bypass-adjacent behaviour this invariant exists to rule
   * out, just inverted.)
   *
   * The MMN branch is the one exception (ai-review HIGH, issues #346/#347):
   * while the authoritative `/security/products-summary` lookup is still in
   * flight, OR has FAILED, the fallback is NOT available — the agent cannot
   * mark Pass against data that has not yet been confirmed to exist, nor
   * after an upstream error. It only becomes answerable once the lookup has
   * genuinely resolved (`otherProducts` is a real array).
   */
  function notOnFileFallbackAvailable(branch: NotOnFileBranch): boolean {
    if (branch === 'mothersMaidenName') {
      const ctx = otherProductsContextRef.current;
      return (
        Array.isArray(otherProducts) &&
        ctx !== null &&
        ctx.customerNumber === customerNumber &&
        ctx.tellerId === tellerId &&
        ctx.screenId === config.screenId
      );
    }
    return true;
  }

  /**
   * Whether a not-on-file branch has TERMINALLY failed (as opposed to merely
   * not-yet-answerable). Distinct from `notOnFileFallbackAvailable`: the
   * brief in-flight `'loading'` window is "not yet available" (blocks Pass)
   * but must NOT trip the run's latched Fail — only a genuine `'error'` does.
   */
  function notOnFileFallbackErrored(branch: NotOnFileBranch): boolean {
    if (branch === 'mothersMaidenName') return otherProducts === 'error';
    return false;
  }

  /**
   * Whether a question's pass/fail is satisfied (incl. a legit not-on-file
   * branch). A not-on-file branch is NOT a bypass: the agent must still mark
   * the revealed fallback question (associated products / landline) Pass for
   * the SAME question key. Otherwise the primary on-file Pass/Fail applies
   * (and requires an on-file value).
   */
  function questionPasses(q: SecurityQuestion): boolean {
    if (q.notOnFile && notOnFileValid(q.notOnFile, q)) {
      return (
        notOnFileFallbackAvailable(q.notOnFile) && statuses[q.key] === 'pass'
      );
    }
    // External (TSYS) checks have no on-file value in our data — the agent
    // verifies them externally, so they pass on an explicit Pass mark alone and
    // are exempt from the on-file-value fail-closed rule (#223 / CCF-5). A
    // question that opts in via `agentVerified` (the EDS High recurring-payment
    // "Check Transactions" check, CCF-47/CCF-26 AC "agent can press Check
    // Transactions … marks Pass or Fail" — #307/#360) is likewise agent-verified
    // via its in-app action rather than by comparing a displayed on-file string:
    // on the real gateway `RecurringPayment` is frequently absent (issue #282
    // showed "Not available"), so requiring `hasValue` here would make the
    // agent's Pass never count — no PASS badge / no "Continue to dashboard"
    // (#360). The value, when present, is still shown alongside the button
    // (#307) — only the pass-gating is exempted here.
    //
    // NB the exemption is a deliberate, explicit PER-QUESTION opt-in
    // (`agentVerified`), NOT keyed off the mere presence of a `checkAction`:
    // gating on `q.checkAction` alone would silently fail-open any future
    // check-action question that is meant to stay value-backed (ai-review #360).
    if (q.externalCheck || q.agentVerified) return statuses[q.key] === 'pass';
    // #817 (@dylan219, superseding the #660 N/A treatment): the joint-accounts
    // question is now ALWAYS answerable — the agent must mark Pass/Fail even
    // when the customer has none. When the authoritative `customer/joint-
    // associations` read genuinely resolved to EMPTY (no joint accounts), an
    // explicit Pass alone satisfies it — there is no on-file value to compare,
    // the agent confirms verbally with the customer. Still fail-CLOSED on
    // `'error'`/`'idle'`/`'loading'` (`jointAssociations !== 'empty'`), so an
    // unconfirmed/errored read can never let High Security complete — the #660
    // ai-review HIGH invariant is preserved.
    if (
      q.key === 'joint-accounts' &&
      q.optionalWhenAbsent === true &&
      jointAssociations === 'empty'
    ) {
      return statuses[q.key] === 'pass';
    }
    return statuses[q.key] === 'pass' && hasValue(q);
  }
  function questionFailed(q: SecurityQuestion): boolean {
    if (q.notOnFile && notOnFileValid(q.notOnFile, q)) {
      // An explicit Fail, OR a TERMINALLY errored fallback, is a failure —
      // never silently passable. The mobile branch's fallback never errors,
      // so this reduces to the agent's own explicit Fail mark there. The MMN
      // branch's brief in-flight loading window is deliberately NOT treated
      // as a failure here (only blocks Pass, via
      // `notOnFileFallbackAvailable`/`questionPasses`) — only a genuine
      // upstream error latches the run's Fail.
      return (
        statuses[q.key] === 'fail' || notOnFileFallbackErrored(q.notOnFile)
      );
    }
    return statuses[q.key] === 'fail';
  }

  /**
   * Whether a question is NOT APPLICABLE — genuinely unanswerable, so it must
   * be excluded from the mandatory Pass/Fail completion gate (#660). True only
   * for a question that opts in via `optionalWhenAbsent`, whose authoritative
   * value is absent ("Not available"/"not on file"), AND that offers no other
   * way to answer it: no `notOnFile` branch, no `externalCheck`, and no
   * `agentVerified` in-app check. The EDS High joint-accounts question is the
   * sole such question today — when the customer has no joint accounts the
   * agent has no Pass/Fail to record, so requiring it would block completion
   * forever. Fail-closed by construction: the moment a value IS on file (or the
   * question exposes any answering path) it is fully mandatory again.
   *
   * ai-review HIGH (#660): a cleared/absent `JointAccounts` value is ambiguous
   * — a FAILED / errored / not-yet-loaded `customer/joint-associations` fetch
   * renders "Not available" exactly like a genuine no-joint-accounts success.
   * So `optionalWhenAbsent` alone must NOT fail open. The question may be N/A
   * ONLY when the authoritative read SUCCEEDED with a genuinely EMPTY list
   * (`jointAssociations === 'empty'`); on `'idle'` / `'loading'` / `'error'`
   * (and `'loaded'`) it stays un-satisfied so a failed authoritative check can
   * never let High Security complete. Mirrors the CCF-26/#282 fail-closed rule
   * just above.
   */
  function questionNotApplicable(_q: SecurityQuestion): boolean {
    // #817 (@dylan219, authoritative — supersedes the #660 N/A treatment):
    // the joint-accounts question is NO LONGER treated as Not-Applicable when
    // the customer has none. It is now ALWAYS answerable — the agent must mark
    // Pass/Fail, and the genuinely-empty case renders "No joint accounts on
    // file." while still offering the radios (see `renderMandatory` /
    // `questionPasses`). No question is Not-Applicable on these screens today,
    // so this returns false for all. The #660 fail-closed invariant lives on
    // in `questionPasses` (empty → answerable via Pass; error/idle/loading →
    // not satisfiable, completion stays blocked).
    return false;
  }

  /**
   * Whether this question's authoritative source could NOT be checked (#660) —
   * an errored `customer/joint-associations` read for the joint-accounts
   * opt-in question. Distinct from N/A: the read did not resolve to an
   * authoritative answer at all. Surfaces an explicit "could not check" note
   * (the radios stay so the agent can record a Fail) and, because the value is
   * absent, Pass can never be satisfied — completion stays blocked. Fail-closed
   * (ai-review HIGH, #660).
   */
  function questionUnavailable(q: SecurityQuestion): boolean {
    return (
      q.optionalWhenAbsent === true &&
      // Joint-accounts specific — same coupling as questionNotApplicable
      // (ai-review LOW, #660).
      q.key === 'joint-accounts' &&
      !q.notOnFile &&
      !q.externalCheck &&
      !q.agentVerified &&
      questionsLoaded &&
      !hasValue(q) &&
      jointAssociations === 'error'
    );
  }

  // Whether a choose-group question is selectable. An `externalCheck`
  // (swivel-chair, e.g. Hogan) question has no on-file answer to source by
  // design and is always selectable; the agent verifies it externally and
  // records Pass/Fail directly. Any other question requires an authoritative
  // on-file value — a question with no answer ("Not available"/absent) cannot
  // be chosen toward the requirement (no-bypass).
  const isAvailable = (q: SecurityQuestion): boolean => {
    return q.externalCheck || hasValue(q);
  };

  // Mandatory always-on questions all pass. A genuinely unanswerable question
  // (e.g. EDS High joint accounts when the customer has none — #660) is N/A and
  // does not need a Pass mark it can never receive.
  const mandatoryPass = config.questions.every(
    (q) => questionNotApplicable(q) || questionPasses(q),
  );

  // BUS24 Medium/High knowledge group (SCRN-05/10, CCF-23/42) — issue #334:
  // how many DISTINCT free-text answers the real gateway has on file for the
  // group, via the same `customer/info.SecurityQuestions` enrichment the
  // registration screens already consume (`AdditionalQuestionAnswers`).
  const knowledgeGroupAdditionalAnswerCount = countDistinctAnswers(
    questionData.AdditionalQuestionAnswers,
  );
  // The mock-only `customer/security-questions` op can carry the group's
  // named fields (FirstSchool/FavouriteMusic/EyeColour) directly — the real
  // gateway never does (issue #76). Only fall back to the unordered-list
  // treatment when NONE of the named questions has its own on-file value; a
  // mock customer with named answers keeps the original per-question
  // behaviour unchanged.
  //
  // Issue #600: the free-text DISPLAY-BOX presentation is config-driven and
  // must apply whenever this is an `additionalAnswersUnordered` group whose
  // named questions have no own value — INDEPENDENT of whether any free-text
  // answer is actually on file. When there are none, the box shows
  // "No answers on file" (CCF-23 AC — all answers live in the display box, a
  // missing-detail note; NEVER the per-radio "Not available" / "No answer on
  // file — this question cannot be used." status text). This mirrors the
  // Business24/Broker24 registration screens' free-text box (#305/#311/#458).
  const knowledgeGroupUsesDisplayBox =
    Boolean(config.knowledgeGroup?.additionalAnswersUnordered) &&
    !(config.knowledgeGroup?.questions.some((q) => hasValue(q)) ?? false);
  // Whether the free-text list actually has answers on file — gates
  // selectability/`knowledgeOk` (a KBA cannot be confirmed against nothing).
  const knowledgeGroupUsesUnorderedAnswers =
    knowledgeGroupUsesDisplayBox && knowledgeGroupAdditionalAnswerCount > 0;

  // Grouped "confirm any 1 of N" (CCF-23/42): a question is selected, that
  // question has an authoritative on-file value, and the group is marked Pass.
  // A question with no on-file answer is not a valid selection. In the
  // unordered fallback, "on-file" means at least one distinct free-text
  // answer is on file at all — which specific named question it matches is
  // not knowable (#334, mirroring #305/#311), so every selection is equally
  // valid once any answer is on file.
  //
  // Issue #722 (ai-review HIGH) originally required at least as many DISTINCT
  // on-file answers as there are KBA questions before an `askAll` free-text
  // group could complete. Issue #769 (@dylan219, Option A, 2026-09-08) REVERSES
  // that count heuristic. The real gateway's free-text `SecurityQuestions`
  // field has no delimiter contract: a record whose multiple answers are
  // space-separated (e.g. "EYE'S BLUE NATSCHOOL MOUNTCOLLINS FAVOURITE MUSIC
  // ROCK") splits into a SINGLE distinct answer on commas, so the count gate
  // wrongly blocked customers who plainly DO have several answers on file. We
  // cannot reliably COUNT discrete answers from unstructured free text, so we
  // stop gating on the count: the agent reads the full free-text box and marks
  // each question Pass/Fail themselves, and 2FA still applies — those are the
  // real controls. We require only that SOME answer is on file (a group with
  // genuinely nothing on file is still a dead-end, surfaced by the advisory
  // below). This deliberately supersedes #722 per @dylan219's ruling on #769.
  const knowledgeGroupHasAnswersOnFile =
    knowledgeGroupAdditionalAnswerCount > 0;
  const knowledgeOk = config.knowledgeGroup
    ? config.knowledgeGroup.askAll
      ? config.knowledgeGroup.questions.every(
          (q) =>
            statuses[q.key] === 'pass' &&
            (knowledgeGroupUsesUnorderedAnswers
              ? knowledgeGroupHasAnswersOnFile
              : hasValue(q)),
        )
      : kbaSelected !== '' &&
        kbaStatus === 'pass' &&
        (() => {
          if (knowledgeGroupUsesUnorderedAnswers) {
            return knowledgeGroupAdditionalAnswerCount > 0;
          }
          const q = config.knowledgeGroup.questions.find(
            (x) => x.key === kbaSelected,
          );
          return q ? hasValue(q) : false;
        })()
    : true;

  // #756 / #769 (Option A, @dylan219): Business High Security (SCRN-10, CCF-42)
  // is an `askAll` free-text knowledge group. Following #769 we no longer block
  // on the on-file answer COUNT (see above). The only remaining genuine
  // dead-end is a customer with NO answers on file at all — the box shows "No
  // answers on file" and the agent has nothing to confirm against. This flag
  // detects exactly that state (an unordered `askAll` group with zero on-file
  // answers) so the screen surfaces an explicit "cannot complete via these
  // questions — Fail / refer to branch" advisory instead of a silent stuck
  // screen. A record that DOES have answers on file (however many) is now
  // passable once the agent marks each question Pass.
  const knowledgeInsufficientOnFile =
    Boolean(config.knowledgeGroup?.askAll) &&
    knowledgeGroupUsesDisplayBox &&
    !knowledgeGroupHasAnswersOnFile;

  // "Choose any N of M" (CCF-24): exactly chooseCount chosen, each chosen
  // question available (has an on-file value) + marked Pass.
  const chooseGroup = config.chooseGroup;
  const chooseOk = chooseGroup
    ? chosen.length === chooseGroup.chooseCount &&
      chosen.every((k) => {
        const q = chooseGroup.questions.find((x) => x.key === k);
        return q ? isAvailable(q) && statuses[k] === 'pass' : false;
      })
    : true;

  // #287: the 2FA affordance to skip-with-reason is offered ONLY on HIGH-
  // security variants that require 2FA (the ruling forbids skipping on medium
  // screens; ECL medium is the exception, handled in its own component/
  // increment). A selected skip reason means the agent has recorded why the
  // customer cannot complete 2FA.
  const canSkip2fa = config.requires2fa && config.grantedLevel === 'high';
  // #586: same char-set as the Mobile Number Update team-lead name field.
  const TL_NAME_ALLOWED = /^[\p{L} '-]+$/u;
  const exemptionApprovedByTl =
    skip2faReason === '2FA Exemption Approved by TL';
  const teamLeadNameValid = TL_NAME_ALLOWED.test(teamLeadName.trim());
  // #789: the "2FA Customer Device Issue" reason requires the agent to confirm
  // troubleshooting was completed (a mandatory checkbox) before the skip
  // activates.
  const customerDeviceIssue =
    requiresTroubleshootingConfirmation(skip2faReason);
  // The skip only activates once a reason is chosen — and, for the TL-exemption
  // reason, once a valid approving team lead's name has been entered, and for
  // the customer-device reason, once "Troubleshooting completed" is ticked — so
  // neither can complete without its required confirmation.
  const skip2faActive =
    canSkip2fa &&
    skip2faReason !== '' &&
    (!exemptionApprovedByTl || teamLeadNameValid) &&
    (!customerDeviceIssue || troubleshootingDone);

  // #565/#629 (Figma 2FA-script alignment): the shared SecurityScreen serves
  // BOTH the High variants (EDS/VISA/BUS24 High) and the Medium variants
  // (VISA Medium SCRN-04, BUS24 Medium SCRN-05). All of them now carry the
  // approved EDS-canonical read-back wording — the same wording the EDS Medium
  // sibling (`MediumSecurityScreen.tsx`, #566/MR !838) already uses. #629 closed
  // the remaining gap: the VISA/BUS24 Medium variants previously kept a
  // divergent 2FA script; they now match EDS too, so the 2FA copy is no longer
  // branched on the security level. Wording ONLY — the #287 skip-with-reason
  // function and the OTP handshake are unchanged.
  // Greet by first name, derived the same way the Medium screens do.
  const firstName = (customer.name ?? '').trim().split(/\s+/)[0] ?? '';

  // #287 (@dylan219's ruling): on the OTHER medium-security pages (non-ECL:
  // VISA Medium SCRN-04, BUS24 Medium SCRN-05) the 2FA step must NOT be skipped
  // — "it is up to the agent to manually transition to the high security page
  // and select the reason for non 2FA completion from the drop down". So on a
  // medium variant that requires 2FA we surface a guided transition to this
  // page's own relevant HIGH page (where the skip-with-reason dropdown lives),
  // rather than leaving the agent at the no-2FA dead-end. (ECL medium is the
  // exception — a separate component handled in a follow-up increment.)
  const mediumHighTransition =
    config.requires2fa && config.grantedLevel === 'medium'
      ? MEDIUM_SECURITY_PAGES.find((p) => p.id === config.screenId)
      : undefined;

  // The 2FA piece is satisfied when the OTP passed OR — on a high-security
  // screen — the agent has selected a skip reason ("assume that piece is passed
  // as soon as the agent selects a reason to skip", #287) OR the same-call OTP
  // was CARRIED OVER and RECORDED SERVER-SIDE (#456 — the server run's 2FA step
  // is satisfied without a fresh OTP). Variants without 2FA are trivially
  // satisfied. `twoFaCarriedOver` is only ever true AFTER the server confirmed
  // the carry, so it can never fail-open the PASS.
  const twoFaOk = config.requires2fa
    ? otpStatus === 'pass' || skip2faActive || twoFaCarriedOver
    : true;

  // #280 (CCF-16/26): the 2FA OTP is delivered by SMS to the customer's
  // registered mobile — CCF-16 sends an OTP "for all customers who have an
  // Online Card Number" via SMS. With NO mobile on file there is nothing to
  // send the code to, so offering an enabled "Generate OTP" is a dead action
  // (the gateway rejects the empty number and the agent only sees a "could not
  // send" toast). Parity with the SCRN-03 Medium Security screen, which already
  // disables the control + shows inline guidance when no mobile is on file.
  //
  // Mobile presence is read from the SAME authoritative `/security/questions`
  // data the on-screen "Mobile number:" question renders (`MobileNumber`), so
  // the button gate and the "not on file → landline" branch agree. The OTP
  // stays gated until the current customer's question data has loaded (no
  // fail-open on loading/error state — parity with SCRN-03's `mobileReady`).
  const hasMobileOnFile = Boolean(questionData.MobileNumber?.trim());
  const mobileReady = questionsLoaded && hasMobileOnFile;
  const mobileMissing = questionsLoaded && !hasMobileOnFile;

  // #572 (@dylan219): the non-completion list shows EXACTLY the six sanctioned
  // options everywhere it appears, regardless of whether a mobile is on file
  // (none of the six is mobile-presence-specific — the old #398 "No mobile
  // number on file" option was removed). `hasMobileOnFile` is retained only for
  // the surrounding `mobileMissing` visual emphasis below.
  const availableSkip2faReasons = skip2faReasonsFor(hasMobileOnFile);

  // Any failure (identity / knowledge group / a chosen mortgage question / OTP).
  const anyFailed =
    config.questions.some(questionFailed) ||
    (config.knowledgeGroup
      ? config.knowledgeGroup.askAll
        ? config.knowledgeGroup.questions.some(
            (q) => statuses[q.key] === 'fail',
          )
        : kbaStatus === 'fail'
      : false) ||
    (chooseGroup ? chosen.some((k) => statuses[k] === 'fail') : false) ||
    otpStatus === 'fail' ||
    otpBlocked;

  // #840: the overall result recomputes LIVE from the current Pass/Fail
  // selections — a mis-clicked question Fail can be corrected back to Pass and
  // the overall verdict + downstream actions follow (no one-way latch). A
  // genuine 2FA OTP gateway lockout stays TERMINAL because `anyFailed` includes
  // `otpStatus === 'fail'` / `otpBlocked`, which are server-side states a
  // question radio flip does NOT clear.

  // #263: the FAIL verdict must not be PRESENTED until ALL required security
  // steps have been completed — every mandatory question answered (pass/fail),
  // the knowledge / choose group resolved where present, AND (where the variant
  // requires it) the 2FA step at a TERMINAL outcome. A step still pending shows
  // in-progress, never a premature Fail. The latched-failure logic is unchanged;
  // this only gates the FAIL badge + branch-referral presentation.
  const mandatoryAnswered = config.questions.every(
    (q) => questionNotApplicable(q) || questionPasses(q) || questionFailed(q),
  );
  const knowledgeAnswered = config.knowledgeGroup
    ? config.knowledgeGroup.askAll
      ? config.knowledgeGroup.questions.every(
          (q) => statuses[q.key] === 'pass' || statuses[q.key] === 'fail',
        )
      : kbaStatus !== null
    : true;
  // Choose group: the required number are chosen and each chosen one answered.
  const chooseAnswered = chooseGroup
    ? chosen.length === chooseGroup.chooseCount &&
      chosen.every((k) => statuses[k] === 'pass' || statuses[k] === 'fail')
    : true;
  // The 2FA/OTP step has reached a terminal outcome (passed, failed terminally
  // after 3 attempts, or — on a high-security screen — skipped with a recorded
  // reason, which #287 treats as passed). Not required for variants without 2FA.
  const twoFaComplete = config.requires2fa
    ? otpStatus === 'pass' ||
      otpStatus === 'fail' ||
      otpBlocked ||
      skip2faActive ||
      twoFaCarriedOver
    : true;
  const allStepsComplete =
    questionsLoaded &&
    mandatoryAnswered &&
    knowledgeAnswered &&
    chooseAnswered &&
    twoFaComplete;
  // Present FAIL only once every step is complete and the run has failed.
  // #840: driven by the LIVE `anyFailed` (not a latch) so correcting a
  // mis-clicked Fail back to Pass clears the FAIL presentation; a genuine OTP
  // lockout keeps it (included in `anyFailed`).
  const showFail = anyFailed && allStepsComplete;

  // Every mandatory check passes, the second factor is satisfied (where
  // required) and no failure has occurred this run. Fail closed on the
  // question data: do not allow an upgrade before the authoritative
  // /security/questions response has loaded. Crucially, the loaded data /
  // statuses must belong to the CURRENT customer+config: on a customer swap the
  // component re-renders with the new identity before the passive reset effect
  // runs, so without this key check the previous customer's passing statuses
  // could momentarily satisfy the gate for the wrong customer.
  const stateMatchesCustomer = verificationStateMatchesCustomer(
    loadedRunKey,
    customerNumber,
    config.screenId,
  );
  const allChecksPass =
    stateMatchesCustomer &&
    questionsLoaded &&
    mandatoryPass &&
    knowledgeOk &&
    chooseOk &&
    twoFaOk &&
    !anyFailed;

  // Issues #369 / #370: when the agent has begun but the run is neither a PASS
  // (Continue offered) nor a presented FAIL, list exactly which steps are still
  // outstanding — otherwise the agent hits a silent dead-end with no verdict and
  // no Continue button (the reported defect: "no button to continue and no pass
  // or fail indicator"). The classic trigger is a high-security customer with NO
  // mobile on file: the mobile identity question shows only a "not on file" tick
  // (no Pass/Fail radio until it is ticked), and the OTP is skipped — so a
  // seemingly "all Pass" screen never completes and gives no feedback as to why.
  // This is guidance only: it changes NO gate and never bypasses the no-bypass
  // invariant — it simply names the remaining work so the agent can finish and
  // reach the existing PASS/Continue affordance, aligning every high-security
  // screen (the tester's "please align with other security screens").
  const outstandingSteps: string[] = [];
  if (questionsLoaded) {
    if (!mandatoryAnswered) {
      // Name the specific not-on-file identity questions that are the usual
      // silent dead-end (mobile / mother's maiden name), so the agent knows to
      // tick the "not on file" box to reveal that question's Pass/Fail.
      const branchOutstanding = config.questions.some(
        (q) =>
          q.notOnFile &&
          !hasValue(q) &&
          !notOnFile[q.notOnFile] &&
          !(questionPasses(q) || questionFailed(q)),
      );
      outstandingSteps.push(
        branchOutstanding
          ? 'Answer every security question (Pass or Fail). For a detail that is not on file, tick its "not on file" box to record the alternative answer.'
          : 'Answer every security question (Pass or Fail).',
      );
    }
    // When the KBA answers on file are insufficient (#756), the generic
    // "mark it Pass or Fail" step is misleading (no Pass mark can ever complete
    // the run) — the dedicated `knowledgeInsufficientOnFile` advisory below
    // guides the agent to Fail / refer instead, so suppress it here.
    if (
      config.knowledgeGroup &&
      !knowledgeAnswered &&
      !knowledgeInsufficientOnFile
    ) {
      outstandingSteps.push(
        'Select a knowledge question and mark it Pass or Fail.',
      );
    }
    if (chooseGroup && !chooseAnswered) {
      outstandingSteps.push(
        `Choose ${chooseGroup.chooseCount} of the additional questions and mark each Pass or Fail.`,
      );
    }
    if (config.requires2fa && !twoFaComplete) {
      outstandingSteps.push(
        canSkip2fa
          ? 'Complete 2FA, or select a reason to skip it if the customer cannot receive an OTP.'
          : 'Complete 2FA.',
      );
    }
  }
  // Only surface the guidance once the agent has actually started the run (so it
  // does not shout on a freshly loaded, untouched screen), and only while there
  // is no verdict yet (no PASS, no presented FAIL).
  const runStarted =
    Object.keys(statuses).length > 0 ||
    chosen.length > 0 ||
    kbaStatus !== null ||
    otpSent ||
    skip2faReason !== '';
  const showOutstanding =
    questionsLoaded &&
    runStarted &&
    !allChecksPass &&
    !showFail &&
    outstandingSteps.length > 0;

  function bankHeaderParams(): { header: { 'X-Teller-Id': string } } {
    return { header: { 'X-Teller-Id': tellerId } };
  }

  function ensureContext(): boolean {
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
      toast.error('No verified customer in context. Complete ID&V first.');
      return false;
    }
    return true;
  }

  // CCF-112 (#477): record the security screen's TERMINAL outcome (Passed /
  // Failed) as a Non-API business event in Call History. Standard Medium
  // variants (grantedLevel 'medium') record `Medium Security Result`; the
  // high variants (grantedLevel 'high') record `High Security Result` — the
  // BFF derives the CCRS-1546 wording from `securityLevel`. The Failed outcome
  // is a pure client-side screen state (no gateway call), so — mirroring the
  // email-template event (#434) — BOTH outcomes are fired best-effort here from
  // the client, exactly once per terminal outcome. The once-guard is keyed to
  // the customer + screen so it resets on a context swap (parity with the reset
  // effect above), and never breaks the flow on a history-write failure.
  //
  // Stale-outcome-crosses-customers guard: this effect runs BEFORE the passive
  // customer-change reset effect below (declaration order), so on a context swap
  // the component has already re-rendered with the new `customerNumber` while
  // `allStepsComplete`/`showFail` can still reflect the PREVIOUS customer's
  // finished run. Posting then would write a false Security Result against the
  // WRONG CIF (audit/call-history corruption — the #243/#244/#422 stale-state
  // bug class). We therefore only act when the current terminal state provably
  // belongs to the customer in context: `stateMatchesCustomer` ties the loaded
  // run (`loadedRunKey`/`securityRunKey`) to the current customer+screen, so a
  // stale prior-customer `showFail` cannot fire until the NEW customer's own
  // authoritative data has loaded (which re-stamps `loadedRunKey`).
  const securityEventFiredRef = useRef<string | null>(null);
  useEffect(() => {
    const guardKey = securityRunKey(customerNumber, config.screenId);
    if (securityEventFiredRef.current === guardKey) return;
    if (!customerNumber || !stateMatchesCustomer) return;
    // Terminal Pass = every check passes; terminal Fail = every step complete
    // but the run failed (`showFail`). Neither fires while a step is pending.
    const outcome: 'Passed' | 'Failed' | null = allChecksPass
      ? 'Passed'
      : showFail
        ? 'Failed'
        : null;
    if (!outcome) return;
    securityEventFiredRef.current = guardKey;
    void api
      .POST('/calls/security-event', {
        params: bankHeaderParams(),
        body: {
          customerNumber,
          securityLevel: config.grantedLevel,
          outcome,
        },
      })
      .catch(() => {
        // Best-effort: a Call-History write failure must never break the flow.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allChecksPass,
    showFail,
    stateMatchesCustomer,
    customerNumber,
    config.screenId,
  ]);

  // Reset ALL screen-local verification state whenever the identified customer
  // changes — a pass / loaded data / OTP pass must never survive a context swap
  // and be applied to a DIFFERENT customer (parity with ECL). Clearing
  // `questionsLoaded` re-arms the fail-closed gate until the new customer's data
  // has loaded.
  //
  // Issue #344: this effect fires on every mount, including the very FIRST one
  // — which would otherwise immediately clobber the state just restored from
  // `restoredSecurity` (seeded into the `useState` initial values above) back
  // to blank. `skipInitialReset` is a one-shot guard that skips exactly that
  // first run when there IS a restored snapshot to apply; any LATER genuine
  // customer/screen change still resets as before.
  const skipInitialReset = useRef(Boolean(restoredSecurity.current));
  useEffect(() => {
    if (skipInitialReset.current) {
      skipInitialReset.current = false;
      return;
    }
    setStatuses({});
    setCardNumber('');
    setNotOnFile({ mothersMaidenName: false, mobile: false });
    setOtherProducts('idle');
    setKbaSelected('');
    setKbaStatus(null);
    setChosen([]);
    setQuestionData({});
    setQuestionsLoaded(false);
    setLoadedRunKey(null);
    setOtpSent(false);
    setMaskedMobile('');
    setOtpCode('');
    setOtpStatus(null);
    setOtpAttemptsRemaining(MAX_OTP_ATTEMPTS);
    setOtpExpired(false);
    setOtpBlocked(false);
    setOtpError(null);
    setSkip2faReason('');
    setTeamLeadName('');
    setTeamLeadNameError('');
  }, [customerNumber, config.screenId]);

  // Issue #344: the restore flag is a strict one-shot — `BrowserRouter`
  // persists route state in `window.history.state`, which would otherwise
  // wrongly re-trigger a restore on a later reload of the same URL. Strip it
  // from the current history entry once read (mirrors
  // `TransactionsScreen.tsx`'s identical `restore`-flag handling, issue #55).
  // A non-restoring mount also clears any stale snapshot so it can't leak into
  // an unrelated later run.
  useEffect(() => {
    if (!restoredSecurity.current) clearSecurityRunSnapshot();
    if (
      (location.state as { restoreSecurityRun?: boolean } | null)
        ?.restoreSecurityRun
    ) {
      navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #412 increment 2 (@dylan219's 2026-07-17 steer): continuously persist the
  // in-progress run — bound to the SAME customer + variant + live CALL — so a
  // RETURN to High Security within that call (e.g. the SCRN-26 Mobile Number
  // Update High-Security block's "Go to High Security", or any re-entry) resumes
  // exactly where the agent left off: previously-answered questions are
  // pre-populated and, if 2FA was completed earlier on this call, it stays
  // marked complete. The agent never has to redo work they already did on the
  // live call.
  //
  // Bound to `conversationId` (the server-resolved Genesys interaction, #357/
  // #201) so the snapshot belongs to THIS call: a fresh / different call, or a
  // session with no conversation, never inherits it (`loadSecurityRunSnapshot`
  // fails closed). Only persisted once the agent has actually started the run
  // (something to preserve) — a pristine, untouched screen writes nothing.
  //
  // This is purely a client-side UX pre-fill; it is NOT a verification boundary.
  // `/security/high/complete` still requires an ACTIVE server-side High Security
  // run whose 2FA step was satisfied server-side (`run.otpValidated` / a
  // sanctioned skip), and the servicing write stays `requireVerifiedHigh`-gated
  // — so re-showing a passed OTP in the form can never, by itself, elevate the
  // session.
  useEffect(() => {
    // No call to bind to ⇒ do not persist (offline / standalone). The
    // Check-Transactions round trip does its own explicit save + restore and
    // does not depend on this effect.
    if (!conversationId) return;
    if (!customerNumber) return;
    // The card-number entry alone does NOT count as a started run: the PAN is
    // never persisted (#412), so there is nothing to preserve from it.
    const runStarted =
      Object.keys(statuses).length > 0 ||
      chosen.length > 0 ||
      kbaStatus !== null ||
      otpSent ||
      otpStatus !== null ||
      skip2faReason !== '';
    if (!runStarted) return;
    saveSecurityRunSnapshot({
      runKey: securityRunKey(customerNumber, config.screenId),
      conversationId,
      statuses,
      notOnFile,
      kbaSelected,
      kbaStatus,
      chosen,
      skip2faReason,
      otpStatus,
      otpBlocked,
      otpSent,
      maskedMobile,
      otpAttemptsRemaining,
    });
  }, [
    conversationId,
    customerNumber,
    config.screenId,
    statuses,
    notOnFile,
    kbaSelected,
    kbaStatus,
    chosen,
    skip2faReason,
    otpStatus,
    otpBlocked,
    otpSent,
    maskedMobile,
    otpAttemptsRemaining,
  ]);

  // Fetch the identity-question data once we have a customer + Teller ID.
  useEffect(() => {
    let cancelled = false;
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    // The identity this fetch is for; stamped onto the state when it resolves so
    // verification is bound to the customer it was actually produced against.
    const runKey = securityRunKey(customerNumber, config.screenId);
    void (async () => {
      try {
        const { data, response } = await api.POST('/security/questions', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        });
        if (!response.ok || !data) return;
        if (!cancelled) {
          // JointAccounts is authoritatively sourced from
          // /security/joint-associations (the real gateway op) — NEVER from this
          // mock-only security-questions op. Strip it here so a stale/legacy
          // value cannot satisfy the SCRN-07 check if the authoritative call
          // fails (fail-closed, no-bypass — #282). Preserve any value the
          // authoritative effect has already set.
          const next = { ...(data as SecurityQuestionsData) };
          delete next.JointAccounts;
          setQuestionData((prev) => ({
            ...next,
            ...(prev.JointAccounts !== undefined
              ? { JointAccounts: prev.JointAccounts }
              : {}),
          }));
          setQuestionsLoaded(true);
          setLoadedRunKey(runKey);
        }
      } catch {
        // Non-fatal: the screen still renders labels with "Not available".
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerNumber, tellerId, config.screenId]);

  // Begin the server-side High Security RUN (issue #363) when a HIGH variant
  // opens for an identified customer. This is the seam the BFF uses to
  // distinguish a genuine high-security flow from ordinary medium ID&V: only the
  // 2FA step (OTP validate / sanctioned skip) performed while this run is active
  // can satisfy `/security/high/complete`. Best-effort — the server still
  // fail-closes at completion if the run was not recorded.
  //
  // #456 (medium→high crossover, @dylan219's #412 rule): when a passed OTP was
  // carried over from a run earlier on THIS call (the client-only candidate),
  // AFTER the run is begun ask the BFF to record the run's 2FA step from the
  // SERVER-OBSERVED same-call OTP (`/security/high/carry-2fa`). ONLY on success
  // do we hide the OTP/skip UI ("2FA already completed on this call") — so the
  // agent never redoes 2FA yet `/security/high/complete` can still succeed.
  //
  // FAIL CLOSED on a REFUSAL (ai-review #456 MEDIUM): the restored candidate
  // seeded the live `otpStatus` to `'pass'`, which disables Generate OTP / the
  // OTP input / Validate / the skip selector. If the server refuses the carry
  // (OTP not observed on THIS call / no run), that leaves the agent looking at a
  // 2FA path they cannot use AND `/security/high/complete` would still 403. So on
  // a refusal we RESET the restored 2FA state to an actionable one (clear the
  // candidate `pass` + any sent/masked-mobile carryover) — the OTP/skip controls
  // re-enable and the agent completes 2FA the ordinary way. The reset is guarded
  // to the still-pending candidate so a genuine in-run OTP the agent completes
  // here is never clobbered.
  useEffect(() => {
    if (grantedLevel !== 'high') return;
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    let cancelled = false;
    void (async () => {
      // Forward the variant identifier (the RBAC screen id) so the BFF can
      // decide — from its OWN server-side allow-list — whether this is a no-2FA
      // high run: only SCRN-06 Authorised VISA User (CCF-25 — no OTP by design,
      // issue #554) is allow-listed, so its `/security/high/complete` waives the
      // 2FA-satisfied check. Every other variant begins a 2FA-bearing run that
      // still requires a server-observed OTP. The client never sends a
      // `requires2fa` boolean, so it cannot begin an arbitrary run as no-2FA.
      const started = await beginHighSecurity(
        customerNumber,
        tellerId,
        config.screenId,
      );
      if (cancelled) return;
      // Only attempt the carry when the medium run's passed OTP was restored for
      // THIS call (the candidate) and the run was begun — never on a client flag
      // alone. The server is the authority on whether the same-call OTP counts.
      if (!twoFaCarryCandidate.current) return;
      const carried = started
        ? await carryHighSecurity2fa(customerNumber, tellerId)
        : false;
      if (cancelled) return;
      // The candidate is resolved either way — do not re-attempt on a later
      // re-render.
      twoFaCarryCandidate.current = false;
      if (carried) {
        setTwoFaCarriedOver(true);
        return;
      }
      // Refused (or the run never began): the restored `pass` was NOT confirmed
      // server-side, so it must not disable the 2FA path. Reset to actionable so
      // the agent can generate/validate an OTP (or skip) the ordinary way.
      setOtpStatus((prev) => (prev === 'pass' ? null : prev));
      setOtpSent(false);
      setMaskedMobile('');
    })();
    return () => {
      cancelled = true;
    };
    // `config.screenId` is stable per screen, so it never re-triggers this
    // effect — listed only to satisfy exhaustive-deps.
  }, [grantedLevel, customerNumber, tellerId, config.screenId]);

  // EDS High-Security joint-accounts (CCF-26): the "Have you any joint accounts
  // held in PTSB?" question is sourced from the REAL gateway
  // `customer/joint-associations` op (not the mock-only security-questions op,
  // which the real PTSB gateway 500s — issue #76/#282). Fetch the customer's
  // joint associations and merge the confirmation string(s) — last-4 + holder
  // name(s) — into `questionData.JointAccounts`, which the joint-accounts
  // question renders. Only runs for variants that ASK the joint-accounts
  // question (SCRN-07); other variants skip the call. Best-effort: a failure /
  // no joint accounts leaves the question showing "Not available" (the agent
  // then marks it Fail — no-bypass).
  const asksJointAccounts = useMemo(
    () => config.questions.some((q) => q.key === qJointAccounts.key),
    [config.questions],
  );
  useEffect(() => {
    let cancelled = false;
    if (!asksJointAccounts) return;
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    // Authoritative source for the joint-accounts confirmation. Clear any
    // prior/legacy JointAccounts value up-front so that if this call fails or
    // returns nothing, the question falls back to "Not available" and CANNOT be
    // passed on stale data supplied by another source (fail-closed, no-bypass —
    // ai-review #282).
    setQuestionData((prev) =>
      prev.JointAccounts === undefined
        ? prev
        : { ...prev, JointAccounts: undefined },
    );
    // #660: re-enter the "not yet resolved" outcome for THIS fetch. Until it
    // genuinely resolves the question fails closed (it can't be N/A while
    // `'loading'`), so a stale `'empty'` from a prior customer/tick can never
    // outlive the request it belonged to.
    setJointAssociations('loading');
    void (async () => {
      try {
        const { data, response } = await api.POST(
          '/security/joint-associations',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        // Non-OK / no-data: leave JointAccounts cleared → "Not available",
        // cannot be marked Pass. #660: this is an ERROR outcome (fetch failed /
        // BFF non-OK), DISTINCT from an authoritative empty list — so the
        // question stays un-satisfied and completion blocked (fail-closed), it
        // must NOT be treated as Not applicable.
        if (!response.ok || !data) {
          setJointAssociations('error');
          return;
        }
        // #660: a 200 with a missing / non-array `JointAssociations` is NOT an
        // authoritative empty read — it is a malformed response. Treat it as an
        // ERROR (fail-closed) so the question can never become N/A on
        // unavailable authoritative data.
        if (!Array.isArray(data.JointAssociations)) {
          setJointAssociations('error');
          return;
        }
        const rows = data.JointAssociations;
        const formatted = rows.map((r) => {
          const acct = r.AccountLast4 ? `••${r.AccountLast4}` : '';
          const desc = r.AccountDescription ? `${r.AccountDescription} ` : '';
          const holder = r.HolderName ? ` — ${r.HolderName}` : '';
          return `${desc}${acct}${holder}`.trim();
        });
        // #660: record the authoritative OUTCOME. `'empty'` (a genuine
        // no-joint-accounts success) is the ONLY state the question may fail
        // open to N/A; `'loaded'` means there ARE joint accounts, so it is
        // fully answerable/mandatory.
        setJointAssociations(formatted.length === 0 ? 'empty' : 'loaded');
        // Merge into the security-questions data the joint-accounts question
        // reads. Preserve any other fetched fields.
        setQuestionData((prev) => ({ ...prev, JointAccounts: formatted }));
      } catch {
        // Non-fatal for the page, but an ERROR for this authoritative check:
        // leave JointAccounts cleared → the question renders "Not available"
        // and stays un-satisfied (fail-closed, #660) — never N/A.
        if (!cancelled) setJointAssociations('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [asksJointAccounts, customerNumber, tellerId, config.screenId]);

  // Issues #346/#347: which variants offer the MMN-not-on-file "other
  // products" fallback (SCRN 7/8/10 — the only variants with a
  // `mothersMaidenName` not-on-file branch in their question set).
  const asksMothersMaidenName = useMemo(
    () => config.questions.some((q) => q.notOnFile === 'mothersMaidenName'),
    [config.questions],
  );

  // Issues #346/#347: fetch the customer's ACTUAL other products/accounts for
  // the "Do you have any other products or accounts with PTSB?" fallback —
  // REUSES `/security/products-summary` (the same real `customer/accounts/
  // list` gateway op the Dashboard sources its account tiles from; no new
  // gateway call), rendered as discrete list items (the same convention the
  // joint-accounts question above already uses). Only fetched once the agent
  // has ticked the "not on file" branch — not eagerly on every screen load.
  // A non-OK response or a thrown error moves the state to `'error'` — NOT
  // silently back to `'idle'` — so the screen can show an explicit "could not
  // retrieve" message rather than quietly falling back to a weaker, made-up
  // script the agent could rubber-stamp a Pass against (ai-review HIGH,
  // raised independently on both sibling changes). Pass is blocked until the
  // lookup genuinely resolves — see `notOnFileFallbackAvailable` — and the
  // agent can always record an explicit Fail once the authoritative data
  // can't be confirmed.
  useEffect(() => {
    let cancelled = false;
    if (!asksMothersMaidenName || !notOnFile.mothersMaidenName) return;
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    // ai-review HIGH (issues #346/#347): setting to `'loading'` unconditionally
    // clears any PRIOR resolved result at the START of every new lookup — an
    // array left over from an earlier successful fetch is exactly what
    // `notOnFileFallbackAvailable` treats as "answerable" (`Array.isArray`),
    // so leaving it in place here would let the agent mark Pass against stale
    // data during THIS fetch's in-flight window (e.g. after untick/re-tick, or
    // a teller/customer-context refetch). `'loading'` re-enters the "not yet
    // resolved" state, which keeps Pass disabled until THIS fetch genuinely
    // resolves or fails.
    setOtherProducts('loading');
    // ai-review HIGH (issue #346, 3rd finding): also clear the context tag
    // immediately — belt-and-braces alongside the `'loading'` state above, so
    // a stale context can never outlive the request it belongs to even if
    // some future change reads the ref before checking `Array.isArray`.
    otherProductsContextRef.current = null;
    const requestContext = {
      customerNumber,
      tellerId,
      screenId: config.screenId,
    };
    void (async () => {
      try {
        const { data, response } = await api.POST(
          '/security/products-summary',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (!response.ok || !data) {
          setOtherProducts('error');
          return;
        }
        // Tag the resolved result with the context it was actually fetched
        // for BEFORE publishing it, so the very same render that sees the
        // new array also sees the matching context (`notOnFileFallbackAvailable`
        // reads both from the same commit).
        otherProductsContextRef.current = requestContext;
        // Issue #604 fail-safe: when the OTHER-products read was unavailable
        // the list may be incomplete (accounts-only), so it must NOT be read
        // back as the customer's authoritative full product set. Publish an
        // empty result so the screen falls back to the static confirmation
        // list rather than presenting a partial, authoritative-looking one.
        setOtherProducts(data.ProductsUnavailable ? [] : (data.Products ?? []));
      } catch {
        if (!cancelled) setOtherProducts('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    asksMothersMaidenName,
    notOnFile.mothersMaidenName,
    customerNumber,
    tellerId,
    config.screenId,
  ]);

  function setStatus(key: string, v: 'pass' | 'fail'): void {
    setStatuses((s) => ({ ...s, [key]: v }));
  }

  function toggleNotOnFile(branch: NotOnFileBranch, key: string): void {
    setNotOnFile((s) => ({ ...s, [branch]: !s[branch] }));
    // Clear the field's pass/fail when toggling the branch.
    setStatuses((s) => ({ ...s, [key]: null }));
    // ai-review HIGH (issue #346): deselecting (or reselecting) the MMN
    // not-on-file branch must drop any previously-resolved `otherProducts`
    // immediately — otherwise a stale authoritative result from a PRIOR tick
    // could remain "answerable" (`notOnFileFallbackAvailable`) across an
    // untick/re-tick, or briefly outlive the branch being untied (there is a
    // render between this synchronous update and the refetch effect below
    // observing the new `notOnFile.mothersMaidenName` and resetting to
    // `'loading'`). The refetch effect is the authoritative source once the
    // branch is (re)ticked; this just guarantees there is never a window with
    // a stale resolved value sitting around unticked.
    if (branch === 'mothersMaidenName') {
      setOtherProducts('idle');
    }
  }

  /** Toggle a choose-group question into / out of the chosen set (CCF-24). */
  function toggleChosen(q: SecurityQuestion): void {
    if (!isAvailable(q)) return;
    setChosen((prev) => {
      if (prev.includes(q.key)) {
        // Deselecting also clears its recorded pass/fail.
        setStatuses((s) => ({ ...s, [q.key]: null }));
        return prev.filter((k) => k !== q.key);
      }
      if (chooseGroup && prev.length >= chooseGroup.chooseCount) return prev;
      return [...prev, q.key];
    });
  }

  async function handleGenerateOtp(): Promise<void> {
    if (!ensureContext()) return;
    setGenerating(true);
    try {
      const { data, error, response } = await api.POST(
        '/security/otp/generate',
        {
          params: bankHeaderParams(),
          body: { customerNumber },
        },
      );
      if (response.status === 403) {
        // #788 (CCF-16) — a terminal OTP lockout (#639) fails closed with
        // 403 `{ error: 'otp_locked_out' }`. Route it to the dedicated inline
        // CCF-16 block alert (not the generic RBAC/teller toast).
        if (isOtpLockedOut(response.status, error)) {
          setOtpBlocked(true);
          return;
        }
        toast.error('Forbidden: check Teller ID and permissions.');
        return;
      }
      const body = (data ?? { sent: false }) as OtpGenerateResult;
      if (body.sent) {
        setOtpSent(true);
        setMaskedMobile(body.maskedMobile ?? '');
        setOtpStatus(null);
        setOtpCode('');
        setOtpExpired(false);
        setOtpBlocked(false);
        setOtpError(null);
        setOtpAttemptsRemaining(body.attemptsRemaining ?? MAX_OTP_ATTEMPTS);
        toast.success(
          body.maskedMobile
            ? `Code sent to ${body.maskedMobile}`
            : 'One-time passcode sent',
        );
      } else if (body.blocked) {
        setOtpBlocked(true);
        toast.error(
          body.message ??
            'OTP generation is blocked for 24 hours after 3 failed attempts.',
        );
      } else {
        // Send Code (generate) failed — surface the gateway code + message
        // INLINE under the Send Code button (#782), not only as a toast.
        const message = body.message ?? 'Could not send the one-time passcode.';
        setOtpError({ code: body.resultCode, message, source: 'generate' });
        toast.error(message);
      }
    } catch {
      const message = 'Request failed.';
      setOtpError({ message, source: 'generate' });
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  }

  async function handleValidateOtp(): Promise<void> {
    if (!ensureContext()) return;
    if (!/^\d{6}$/.test(otpCode)) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setValidating(true);
    // Clear any prior inline warning at the start of a fresh validate attempt.
    setOtpError(null);
    try {
      const { data, response } = await api.POST('/security/otp/validate', {
        params: bankHeaderParams(),
        body: { customerNumber, otp: otpCode },
      });
      if (response.status === 403) {
        toast.error('Forbidden: check Teller ID and permissions.');
        return;
      }
      const body = (data ?? { valid: false }) as OtpValidateResult;
      if (body.valid) {
        setOtpStatus('pass');
        setOtpError(null);
        toast.success('Two-factor authentication passed.');
        return;
      }
      if (typeof body.attemptsRemaining === 'number') {
        setOtpAttemptsRemaining(body.attemptsRemaining);
      }
      if (body.expired) {
        // CCF-16: the 5-minute window elapsed — issue a new OTP (retryable, not
        // a security failure). Do NOT latch a fail. The expired branch renders
        // its own dedicated inline alert — clear the generic wrong-code warning
        // so they don't double up. #810: prefer the BFF-mapped ruled copy.
        const expiredMessage =
          body.message ??
          'The one-time passcode has expired. Generate a new code.';
        setOtpExpired(true);
        setOtpSent(false);
        setOtpError(null);
        setOtpExpiredMessage(expiredMessage);
        toast.error(expiredMessage);
      } else if (body.blocked) {
        // CCF-16: 3 incorrect attempts — only NOW does 2FA fail terminally. The
        // blocked branch has its own dedicated inline alert — clear the generic
        // wrong-code warning so they don't double up.
        setOtpStatus('fail');
        setOtpBlocked(true);
        setOtpSent(false);
        setOtpError(null);
        toast.error(
          body.message ??
            'Incorrect code 3 times. Security has failed — advise the customer to call again.',
        );
      } else {
        // A non-final wrong code: allow retry — do NOT set otpStatus to 'fail'
        // (which would prematurely trigger the branch-referral). Surface the
        // gateway code + message INLINE beside the Validate button as a
        // non-blocking warning (issue #164, per #80/#82/#94), KEEPING the toast.
        const remaining = body.attemptsRemaining;
        const message =
          body.message ??
          (typeof remaining === 'number'
            ? `Incorrect code. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
            : 'The code does not match. Please retry.');
        setOtpError({ code: body.resultCode, message, source: 'validate' });
        toast.error(message);
      }
    } catch {
      const message = 'Request failed.';
      setOtpError({ message, source: 'validate' });
      toast.error(message);
    } finally {
      setValidating(false);
    }
  }

  /** Commit verification at the variant's granted level and continue (CCF-53). */
  async function completeAndContinue(): Promise<void> {
    if (!allChecksPass) return;
    // Re-assert the verification state belongs to the customer in context RIGHT
    // NOW (not just at the last render): refuse to commit a result produced for
    // a different customer (e.g. a customer swap whose reset has not yet run).
    if (
      !verificationStateMatchesCustomer(
        loadedRunKey,
        customerNumber,
        config.screenId,
      )
    ) {
      toast.error(
        'Customer context changed — please re-verify the current customer.',
      );
      return;
    }
    // For a HIGH variant, record `high` server-side (issue #363) so the
    // high-security servicing writes (SCRN-26 Mobile Number maintenance) are
    // unlocked at the BFF, not just in the client. The client flag drives UX;
    // the server level is the authoritative boundary.
    if (grantedLevel === 'high') {
      // When the 2FA-OTP step was skipped with a sanctioned reason (CCF-107 /
      // #287) rather than validated, pass the reason so the BFF can record `high`
      // via the sanctioned-skip path (no server OTP was performed).
      const result = await commitHighSecurity(
        customerNumber,
        tellerId,
        skip2faActive ? skip2faReason : undefined,
        // No-2FA high variant (SCRN-06 Authorised VISA User / CCF-25, issue
        // #554): record the completed identity checks server-side before
        // completing, so elevation is not granted on `begin` alone (ai-review).
        !config.requires2fa,
        // #586: forward the approving team lead's name for the TL-exemption skip
        // so it is captured in the audited skip record (server re-validates it).
        exemptionApprovedByTl && skip2faActive
          ? teamLeadName.trim()
          : undefined,
        // #789: forward the "Troubleshooting completed" attestation for the
        // "2FA Customer Device Issue" skip so the server can fail-closed require
        // it (the checkbox is client-side UX only; the server re-validates).
        customerDeviceIssue && skip2faActive ? troubleshootingDone : undefined,
      );
      if (!result.ok) {
        // #793 (@dylan219) — a 2FA-exemption / skip attempt on a customer under an
        // active 24h OTP lockout (CCF-16/#639) fails closed. Show 2FA as FAILED
        // and surface the SAME lockout copy the "Send code again" path uses (the
        // `otpBlocked` CCF-16 block), NOT the generic "could not be recorded"
        // error — the exemption cannot rescue a locked-out customer. The lockout
        // itself stays enforced server-side; only the message + 2FA state change.
        if (result.lockedOut) {
          setOtpBlocked(true);
          return;
        }
        // FAIL CLOSED (issue #363): the BFF did not record `high` (403 / network
        // error). Do NOT flip the client to high or navigate — the high-security
        // servicing writes stay gated at the server and the UX must reflect that.
        toast.error(
          'High Security could not be recorded. Please complete the checks again.',
        );
        return;
      }
    }
    // #427: record which security FLOW verified the customer so the banner can
    // name it (e.g. "EDS Medium Security Verified"). Absent ⇒ plain label.
    updateCustomer({
      securityLevel: grantedLevel,
      customerVerification: 'Y',
      verificationVariant: config.verificationLabel,
    });
    // #412 increment 2: the run is complete — drop the in-progress snapshot so a
    // later re-entry starts clean rather than resuming a finished run (the
    // pre-population is for an INCOMPLETE run the agent was bounced away from).
    clearSecurityRunSnapshot();
    toast.success('Customer verified (security passed).');
    // Broker 24 medium security (CCF-318 / #425) routes to the Reset Internet
    // Password screen (SCRN-57) instead of the dashboard; every other variant
    // keeps the dashboard default.
    navigate(config.successRoute ?? '/dashboard');
  }

  /**
   * Handles a question's in-app `checkAction` (currently only the EDS High
   * recurring-payment "Check Transactions" button, CCF-47/#307). Snapshots the
   * in-progress check state (issue #344) so it survives the same-tab
   * navigation, then navigates to Transactions — carrying `fromSecurity` +
   * `returnTo` so that screen shows a "Back to High Security" button. Variants
   * with no registered return route (i.e. every variant except EDS High —
   * `CHECK_ACTION_RETURN_ROUTES`) still navigate, they just don't offer a way
   * back, keeping this a no-op for anything other than SCRN-07's own
   * `checkAction`.
   */
  function handleCheckAction(): void {
    saveSecurityRunSnapshot({
      runKey: securityRunKey(customerNumber, config.screenId),
      // #412 increment 2: also stamp the call so this snapshot doubles as the
      // call-bound resume point (a return to High Security within the same call
      // pre-populates), not only the same-tab Check-Transactions round trip.
      conversationId,
      statuses,
      notOnFile,
      kbaSelected,
      kbaStatus,
      chosen,
      skip2faReason,
      otpStatus,
      otpBlocked,
      otpSent,
      maskedMobile,
      otpAttemptsRemaining,
    });
    const returnTo = CHECK_ACTION_RETURN_ROUTES[config.screenId];
    navigate(
      '/transactions',
      returnTo ? { state: { fromSecurity: true, returnTo } } : undefined,
    );
  }

  const headingId = `${config.screenId.toLowerCase()}-heading`;
  const checkboxClasses =
    'size-[24px] rounded-[4px] border-2 border-[#343f41] accent-[#FF703E]';

  /** Renders a mandatory identity question, with its not-on-file branch. */
  function renderMandatory(q: SecurityQuestion, i: number): React.JSX.Element {
    const value = q.resolve(resolverArgs);
    const onFile = hasValue(q);
    const branch = q.notOnFile;
    const branchValid = branch ? notOnFileValid(branch, q) : false;

    // Simple question (no not-on-file branch): the original layout. An external
    // (TSYS) check shows only the label + hint and NO value — the agent verifies
    // it against TSYS (issue #223 / CCF-5, mirroring the #48 registration fix).
    // A question with an in-app `checkAction` (e.g. "Check Transactions",
    // CCF-47/#307) similarly must not show a stale/absent on-file value — but
    // only when there genuinely ISN'T one: unlike the TSYS questions, this
    // field CAN be genuinely on file, and when it is, #307 deliberately kept
    // it displayed alongside the button. Suppress only the "Not
    // available"/blank fallback (issue #325), not a real value.
    const suppressValue =
      Boolean(q.externalCheck) || (!!q.checkAction && !onFile);
    // #817 (@dylan219): the joint-accounts question drives its display off the
    // authoritative `customer/joint-associations` read outcome. When it
    // genuinely returned no joint accounts, show "No joint accounts on file."
    // (not the bold "Not available") and keep the Pass/Fail radios; while the
    // read is still resolving, show a "Checking…" note. The bold on-file value
    // is suppressed in both cases so the ambiguous "Not available" never shows.
    // (The errored state is handled separately via `questionUnavailable`.)
    const isJointAccounts = q.key === 'joint-accounts';
    const jointEmpty = isJointAccounts && jointAssociations === 'empty';
    const jointPending =
      isJointAccounts &&
      (jointAssociations === 'idle' || jointAssociations === 'loading');
    const infoNote = jointEmpty
      ? 'No joint accounts on file.'
      : jointPending
        ? 'Checking joint accounts…'
        : undefined;
    const suppressJointFallback = jointEmpty || jointPending;
    // Issue #344: the question's own `checkAction.onClick` is an inert
    // placeholder (it can't close over this component's live state — it's a
    // static, shared config object). Override it here with the real handler,
    // which snapshots the in-progress check state before the same-tab
    // navigation. A no-op for every question that doesn't set `checkAction`.
    const checkAction = q.checkAction
      ? { ...q.checkAction, onClick: handleCheckAction }
      : undefined;
    if (!branch) {
      return (
        <React.Fragment key={q.key}>
          {i > 0 && <div className="border-t-2 border-[#dad7d3]" />}
          <IdentityQuestion
            label={labelFor(q)}
            value={suppressValue || suppressJointFallback ? undefined : value}
            listValue={
              suppressValue || suppressJointFallback
                ? undefined
                : q.resolveList?.(resolverArgs)
            }
            hint={q.hint}
            infoNote={infoNote}
            checkAction={checkAction}
            name={q.key}
            status={statuses[q.key] ?? null}
            onChange={(v) => setStatus(q.key, v)}
            notApplicable={questionNotApplicable(q)}
            unavailable={questionUnavailable(q)}
          />
        </React.Fragment>
      );
    }

    const branchKey = `${q.key}-not-on-file`;
    return (
      <React.Fragment key={q.key}>
        {i > 0 && <div className="border-t-2 border-[#dad7d3]" />}
        <div className="flex items-start gap-4">
          <div className="flex flex-col">
            <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
              {labelFor(q)}
            </h3>
            <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-3">
              {onFile ? value : 'Not on file'}
            </p>
            {/* Both the MAIDEN (#626) and the MOBILE (#662 / CCRS-3447) "not on
                file" ticks are ALWAYS offered, even when a value is on file:
                branches enter junk into the maiden field, and a supervisor may
                need to flag a stale/incorrect mobile and verify against the
                landline instead. The agent decides when to use it; the no-bypass
                invariant is preserved by `questionPasses` still requiring an
                explicit Pass on the revealed fallback (products / landline). */}
            {questionsLoaded && (
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id={branchKey}
                  checked={notOnFile[branch]}
                  onChange={() => toggleNotOnFile(branch, q.key)}
                  className={checkboxClasses}
                />
                <label
                  htmlFor={branchKey}
                  className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
                >
                  {branch === 'mothersMaidenName'
                    ? 'Tick if Mother’s maiden name is blank, TBC, NA, unknown, not known etc.'
                    : 'Tick if mobile number not on file'}
                </label>
              </div>
            )}

            {branchValid && branch === 'mothersMaidenName' && (
              <div className="mt-4">
                <div className="border-t border-[#e5e7eb] mb-4" />
                <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-3">
                  Do you have any other products or accounts with PTSB?
                </p>
                {/* Issues #346/#347: sourced from `/security/products-summary`
                    (the same real `customer/accounts/list` gateway op the
                    Dashboard already uses — no new gateway call), rendered as
                    discrete list items (the same convention the joint-
                    accounts question already uses). An `'error'` state
                    renders an explicit "could not retrieve" message rather
                    than a made-up generic script — the agent can still
                    record an explicit Fail (no-bypass), but is never invited
                    to Pass the customer against fabricated categories. */}
                {Array.isArray(otherProducts) ? (
                  otherProducts.length > 0 ? (
                    <>
                      <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-3">
                        {/* Issue #601 / CCRS-3310: wording aligned to the
                            approved Figma (High Security frames) — was
                            "Confirm the following products/accounts held with
                            the customer:". Registration screens keep the older
                            copy (#389); out of scope here. */}
                        Customer&apos;s associated products/accounts:
                      </p>
                      <ul className="list-none m-0 p-0 mb-4 space-y-1">
                        {otherProducts.map((p, idx) => (
                          <li
                            key={`${p.AccountLast4}-${idx}`}
                            className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                          >
                            {formatProductEntry(p)}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-3">
                      No other products or accounts on file.
                    </p>
                  )
                ) : otherProducts === 'error' ? (
                  <p
                    role="alert"
                    className="text-[14px] font-['Arial',sans-serif] text-[#c02020] mb-3"
                  >
                    Unable to retrieve the customer&apos;s product list from the
                    bank systems. Mark Fail if this cannot be confirmed by
                    another means.
                  </p>
                ) : (
                  <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-3">
                    Loading the customer&apos;s products…
                  </p>
                )}
                <div className="mt-4 max-w-[280px]">
                  <PassFailRadios
                    name={`${q.key}-products`}
                    value={statuses[q.key] ?? null}
                    onChange={(v) => setStatus(q.key, v)}
                    // ai-review HIGH (issue #347): Pass is fail-closed unless
                    // the lookup has genuinely resolved (blocks Pass while
                    // loading OR on error). Fail stays available once the
                    // lookup has FAILED (not while merely loading) — the
                    // error copy above explicitly invites the agent to "Mark
                    // Fail if this cannot be confirmed by another means".
                    disabled={{
                      pass: !Array.isArray(otherProducts),
                      fail:
                        otherProducts !== 'error' &&
                        !Array.isArray(otherProducts),
                    }}
                  />
                </div>
              </div>
            )}

            {branchValid && branch === 'mobile' && (
              <div className="mt-4">
                <div className="border-t border-[#e5e7eb] mb-4" />
                <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-3">
                  Can you confirm your landline number?
                </p>
                {/* Issue #601 / CCRS-3310: instructional helper text from the
                    approved Figma (High Security frames), previously missing. */}
                <p className="text-[12px] font-['Arial',sans-serif] text-[#1d252c] mb-3">
                  If Yes - Confirm landline number | If No - This is the correct
                  answer
                </p>
                <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-3">
                  {questionData.Landline?.trim()
                    ? questionData.Landline
                    : 'No landline on file'}
                </p>
                {/* Issue #344 (@dylan219): with no mobile AND no landline on
                    file the agent must still be able to record an explicit
                    Pass/Fail — the "No landline on file" copy above is
                    informational only, it no longer blocks the control (that
                    would be an automatic, uncontrollable fail). Same radios
                    regardless of whether a landline is on file. */}
                <div className="max-w-[280px]">
                  <PassFailRadios
                    name={`${q.key}-landline`}
                    value={statuses[q.key] ?? null}
                    onChange={(v) => setStatus(q.key, v)}
                  />
                </div>
              </div>
            )}
          </div>
          {/* The primary Pass/Fail applies while the value is on file (the
              not-on-file branch provides its own Pass/Fail). */}
          {onFile && (
            <div className="ml-auto mt-5">
              <PassFailRadios
                name={q.key}
                value={statuses[q.key] ?? null}
                onChange={(v) => setStatus(q.key, v)}
              />
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }

  return (
    <main
      className="max-w-[900px] pl-12 pr-12 py-12"
      aria-labelledby={headingId}
    >
      <div className="mb-6 flex items-start justify-between gap-8 flex-wrap">
        <div>
          <h1
            id={headingId}
            className="text-[24px] font-['Arial',sans-serif] font-bold text-[#FF703E] m-0"
          >
            {config.heading}
          </h1>
          {config.sourceNote && (
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mt-2 mb-0">
              {config.sourceNote}
            </p>
          )}
        </div>
        {/* Medium security pages cross-navigate (issue #101 / @dylan219): the
            dropdown offers the OTHER medium pages + this page's own relevant
            high security page. Pure navigation — each target is an existing
            RBAC-gated route, re-checked on arrival (deny-by-default). */}
        {config.mediumNav && (
          <SecurityProcessMenu currentId={config.screenId} />
        )}
        {/* High security pages cross-navigate to the OTHER high security pages
            (issue #397 — @dylan219 option A, reverses #101/#117). Pure
            navigation — each target is an existing RBAC-gated route, re-checked
            on arrival (deny-by-default). */}
        {config.highNav && (
          <SecurityProcessMenu currentId={config.screenId} mode="high" />
        )}
      </div>

      <div className="space-y-6">
        {/* Optional dark "Mandatory selection" heading bar above the mandatory
            identity questions (SCRN-09 / CCF-24 — matches the approved Figma).
            Mirrors the chooseGroup/knowledgeGroup dark heading-bar style. */}
        {config.mandatoryHeading && (
          <div className="bg-[#1D252C] text-white px-6 py-3 inline-block">
            <h2 className="text-[16px] font-['Arial',sans-serif] font-bold m-0">
              {config.mandatoryHeading}
            </h2>
          </div>
        )}
        <InfoBox>
          <div className="space-y-6">
            {/* Optional card-number capture (e.g. 16-digit Visa Credit Card
                number for the Authorised VISA User flow — CCF-25). */}
            {config.cardNumberEntry && (
              <div className="flex flex-col pb-6 border-b-2 border-[#dad7d3]">
                <label
                  htmlFor="visa-card-number"
                  className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                >
                  {config.cardNumberEntry}
                </label>
                <input
                  id="visa-card-number"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={16}
                  value={cardNumber}
                  onChange={(e) =>
                    setCardNumber(e.target.value.replace(/\D/g, ''))
                  }
                  placeholder="Enter the 16-digit card number"
                  className="w-full max-w-[360px] h-[44px] px-3 border-2 border-[#dad7d3] rounded text-[16px] font-['Arial',sans-serif] text-[#1d252c]"
                />
              </div>
            )}

            {/* Mandatory identity questions */}
            <div className="space-y-6">
              {config.questions.map((q, i) => renderMandatory(q, i))}
            </div>
          </div>
        </InfoBox>

        {/* Grouped knowledge block: "confirm any 1 of N" (CCF-23/SCRN-05) or
            "ask all with individual Pass/Fail" (CCF-42/#722/SCRN-10). */}
        {config.knowledgeGroup && (
          <>
            {!config.knowledgeGroup.askAll && (
              <div className="bg-[#1D252C] text-white px-6 py-3 inline-block">
                <h2 className="text-[16px] font-['Arial',sans-serif] font-bold m-0">
                  {config.knowledgeGroup.heading}
                </h2>
              </div>
            )}
            <InfoBox>
              <fieldset className="border-0 m-0 p-0">
                <legend className="sr-only">
                  {config.knowledgeGroup.askAll
                    ? 'Knowledge-based questions — mark each Pass or Fail'
                    : 'Select one knowledge-based question to confirm'}
                </legend>
                {!config.knowledgeGroup.askAll && (
                  <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mb-4">
                    Confirm any one of the following questions, then mark the
                    group Pass or Fail.
                  </p>
                )}
                {knowledgeGroupUsesDisplayBox && (
                  // Issue #334 (mirroring #305/#311's ruling for the identical
                  // registration-screen data): the on-file answers are free
                  // text from `customer/info` with no guaranteed order or 1:1
                  // mapping to the named questions below, so they are shown
                  // ONCE, in the display box, rather than under a specific
                  // question. Issue #600: this display box is the ONLY place
                  // the answer status is shown — when nothing is on file it
                  // reads "No answers on file" (CCF-23 AC), and NO per-radio
                  // "Not available" / "No answer on file" text is rendered
                  // (mirrors the registration screens' empty-case box).
                  <div className="mb-4">
                    <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0 mb-1">
                      Answers on file (free text — order not guaranteed):
                    </p>
                    {(questionData.AdditionalQuestionAnswers ?? []).length ? (
                      <ul className="list-disc pl-5 space-y-1">
                        {(questionData.AdditionalQuestionAnswers ?? []).map(
                          (a, i) => (
                            <li
                              key={i}
                              className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                            >
                              {a}
                            </li>
                          ),
                        )}
                      </ul>
                    ) : (
                      <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                        No answers on file
                      </p>
                    )}
                  </div>
                )}
                {config.knowledgeGroup.askAll ? (
                  // Issue #722 (CCF-42 AC change): ask ALL questions, each with
                  // its own Pass/Fail radio. Group passes only when all are Pass.
                  <div className="space-y-4">
                    {config.knowledgeGroup.questions.map((q, i) => (
                      <React.Fragment key={q.key}>
                        {i > 0 && <div className="border-t border-[#e5e7eb]" />}
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                            {labelFor(q)}
                            {!knowledgeGroupUsesDisplayBox &&
                              q.resolve(resolverArgs) !== undefined && (
                                <>
                                  {' '}
                                  <span className="font-bold">
                                    {q.resolve(resolverArgs)}
                                  </span>
                                </>
                              )}
                          </span>
                          <PassFailRadios
                            name={`kba-${q.key}`}
                            value={statuses[q.key] ?? null}
                            onChange={(v) => setStatus(q.key, v)}
                          />
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <>
                    <div
                      role="radiogroup"
                      aria-label="Knowledge-based question selection"
                      className="space-y-3"
                    >
                      {config.knowledgeGroup.questions.map((q) => {
                        const id = `kba-${q.key}`;
                        const selected = kbaSelected === q.key;
                        // A KBA question with no authoritative on-file answer cannot
                        // be selected (no-bypass: ID&V must not pass on a non-answer).
                        // In the free-text display-box mode (#334/#600) the answer
                        // status lives ONLY in the display box above; the radios
                        // carry no per-question status and stay selectable (which
                        // named question a free-text answer belongs to is not
                        // knowable). The `knowledgeOk` gate still fails closed when
                        // no answer is on file, so a Pass can't be recorded against
                        // nothing.
                        const answerable = knowledgeGroupUsesDisplayBox
                          ? true
                          : hasValue(q);
                        return (
                          <div key={q.key} className="flex flex-col gap-1">
                            <div className="flex items-center gap-3">
                              <input
                                type="radio"
                                id={id}
                                name="kba-select"
                                checked={selected}
                                disabled={!answerable}
                                onChange={() => setKbaSelected(q.key)}
                                className="size-[20px] accent-[#FF703E] disabled:opacity-40"
                              />
                              <label
                                htmlFor={id}
                                className={`text-[16px] font-['Arial',sans-serif] text-[#1d252c] ${answerable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                              >
                                {labelFor(q)}
                                {!knowledgeGroupUsesDisplayBox && (
                                  <>
                                    {' '}
                                    <span className="font-bold">
                                      {q.resolve(resolverArgs)}
                                    </span>
                                  </>
                                )}
                              </label>
                            </div>
                            {/* Issue #600: the per-radio "No answer on file" note is
                                suppressed in the free-text display-box mode — the
                                display box above is the single source of answer
                                status. It still shows in the (mock-only) named-field
                                mode where each question resolves its own value. */}
                            {!knowledgeGroupUsesDisplayBox &&
                              questionsLoaded &&
                              !answerable && (
                                <p
                                  role="note"
                                  className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] ml-8 m-0"
                                >
                                  No answer on file — this question cannot be
                                  used.
                                </p>
                              )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="border-t-2 border-[#dad7d3] mt-6 pt-6 flex items-center gap-6">
                      <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                        Selected question result:
                      </span>
                      <PassFailRadios
                        name="kba-group"
                        value={kbaStatus}
                        onChange={setKbaStatus}
                      />
                    </div>
                    {kbaStatus !== null && kbaSelected === '' && (
                      <p
                        role="alert"
                        className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                      >
                        Select which question you confirmed.
                      </p>
                    )}
                  </>
                )}
              </fieldset>
            </InfoBox>
          </>
        )}

        {/* "Choose any N of M" block (CCF-24 mortgage). */}
        {chooseGroup && (
          <>
            <div className="bg-[#1D252C] text-white px-6 py-3 inline-block">
              <h2 className="text-[16px] font-['Arial',sans-serif] font-bold m-0">
                {chooseGroup.heading}
              </h2>
            </div>
            <InfoBox>
              <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mb-4">
                {`Choose any ${chooseGroup.chooseCount} of the following questions (${chosen.length}/${chooseGroup.chooseCount} chosen), then mark each Pass or Fail.`}
              </p>
              <div className="space-y-6">
                {chooseGroup.questions.map((q, i) => {
                  const available = isAvailable(q);
                  const isChosen = chosen.includes(q.key);
                  const atLimit =
                    !isChosen && chosen.length >= chooseGroup.chooseCount;
                  // An externalCheck (Hogan swivel-chair, CCF-24/#309) question
                  // has no on-file answer to source by design — never render a
                  // value or a "not available" fallback for it, only the
                  // question + its hint (if any) + Pass/Fail, matching the
                  // TSYS externalCheck pattern used elsewhere on this screen.
                  const value = q.externalCheck
                    ? undefined
                    : q.resolve(resolverArgs);
                  return (
                    <React.Fragment key={q.key}>
                      {i > 0 && <div className="border-t border-[#e5e7eb]" />}
                      <div className="flex items-start gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <input
                            type="checkbox"
                            id={`choose-${q.key}`}
                            checked={isChosen}
                            disabled={!available || atLimit}
                            onChange={() => toggleChosen(q)}
                            className={`${checkboxClasses} mt-1 disabled:opacity-40`}
                          />
                          <div className="flex flex-col">
                            <label
                              htmlFor={`choose-${q.key}`}
                              className={`text-[16px] font-['Arial',sans-serif] text-[#1d252c] ${available ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                            >
                              {labelFor(q)}
                              {value !== undefined && (
                                <>
                                  {' '}
                                  <span className="font-bold">{value}</span>
                                </>
                              )}
                            </label>
                            {q.hint && (
                              <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0 mt-1 whitespace-pre-line">
                                {q.hint}
                              </p>
                            )}
                            {questionsLoaded && !available && (
                              <p
                                role="note"
                                className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mt-1 mb-0"
                              >
                                No answer on file — this question cannot be
                                used. Choose another.
                              </p>
                            )}
                          </div>
                        </div>
                        {isChosen && (
                          <div className="ml-auto mt-1">
                            <PassFailRadios
                              name={q.key}
                              value={statuses[q.key] ?? null}
                              onChange={(v) => setStatus(q.key, v)}
                            />
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </InfoBox>
          </>
        )}

        {/* 2FA OTP step (CCF-16) — variants that require it. */}
        {config.requires2fa && (
          <InfoBox className="border-l-[#FF703E] border-l-[6px]">
            <div>
              {/* CCF-16 happy-path step 1: display the customer's mobile
                  number BEFORE a code is sent, so the agent reads it out and
                  the customer confirms it before clicking Generate OTP (per
                  the CCF-23 figma + the SCRN-03 Medium Security sibling,
                  `MediumSecurityScreen.tsx`). Sourced from the same
                  `questionData.MobileNumber` already used for
                  `hasMobileOnFile`/`mobileReady` — no new fetch. Once a code
                  has been sent, prefer the (possibly differently-formatted)
                  `maskedMobile` the gateway echoed back on
                  `/security/otp/generate`, falling back to the full number. */}
              {/* #565/#629: read-back scripting ABOVE the mobile-number line
                  (Figma; matches the EDS Medium sibling MR !838). Now rendered
                  for ALL 2FA variants — High and VISA/BUS24 Medium — so the
                  scripting matches EDS everywhere (#629). */}
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mt-0 mb-4">
                {firstName
                  ? `Thanks ${firstName}, I need to do one more security check. I'll send a code to your mobile phone.`
                  : `Thanks, I need to do one more security check. I'll send a code to your mobile phone.`}
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mt-0 mb-4">
                Can you tell me your phone number please?
              </p>
              <div className="flex items-baseline gap-2 mb-4">
                <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                  Customer mobile number:
                </h3>
                {(maskedMobile ||
                  (mobileReady && questionData.MobileNumber)) && (
                  <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                    {maskedMobile || questionData.MobileNumber}
                  </p>
                )}
              </div>

              {/* #456 (medium→high crossover, @dylan219's #412 rule): when 2FA
                  was already completed earlier on THIS call (a passed OTP carried
                  over from the medium run) AND that carry has been RECORDED
                  SERVER-SIDE (`/security/high/carry-2fa` succeeded — not a
                  client-only restored flag), do NOT make the agent redo it: show
                  a clear "already completed" indicator in place of the OTP entry
                  step. Only reached after the server confirmed the run's 2FA step
                  is satisfied, so `/security/high/complete` can succeed (fixes the
                  ai-review #456 gap where hiding the UI on a client-only flag left
                  the server run unsatisfied → 403). The overall PASS/FAIL outcome
                  is rendered BELOW this block so it shows in BOTH branches. */}
              {twoFaCarriedOver ? (
                <p
                  role="status"
                  className="text-[16px] font-['Arial',sans-serif] font-bold text-[#15803d] mt-0 mb-0"
                >
                  2FA already completed on this call.
                </p>
              ) : (
                <>
                  {/* #280 (CCF-16/26): 2FA cannot proceed without a mobile to send
                  the SMS OTP to. When no mobile is on file, disable Generate OTP
                  and show inline guidance (per @dylan219's #80/#82/#94 inline
                  UX + the SCRN-03 precedent) rather than fail open by sending a
                  code to nothing. When the mobile is not on file the CCF-26
                  landline fallback question above is used in its place. */}
                  {mobileMissing && (
                    <p
                      role="alert"
                      className="text-[16px] font-['Arial',sans-serif] text-[#dc2626] mt-0 mb-4"
                    >
                      No mobile number is on file for this customer, so an OTP
                      cannot be sent.
                      {/* #635: the "complete the alternative security questions
                      above instead" follow-on sentence was removed for all
                      security screens; the transition guidance below tells the
                      agent what to do where 2FA cannot be skipped. */}
                    </p>
                  )}

                  {/* #287 (@dylan219 "Option A with minor additions"): on HIGH-
                  security screens the agent may complete WITHOUT 2FA when the
                  customer cannot receive an OTP (most commonly no mobile on
                  file). They must select a reason; the 2FA piece is then treated
                  as PASSED. Offered on high variants only — the ruling forbids
                  skipping on medium screens (ECL medium, a separate component,
                  is the exception handled in a follow-up). Visually emphasised
                  when the mobile is missing (the reported dead-end). */}
                  {canSkip2fa && (
                    <div
                      className={`mb-4 rounded-md border-2 p-4 ${mobileMissing ? 'border-[#FF703E] bg-[#FFF4EF]' : 'border-[#dad7d3] bg-[#faf9f7]'}`}
                    >
                      <label
                        htmlFor="skip-2fa-reason"
                        className="block text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                      >
                        2FA - Reason code for non completion
                      </label>
                      <select
                        id="skip-2fa-reason"
                        value={skip2faReason}
                        disabled={otpStatus === 'pass' || otpBlocked}
                        onChange={(e) => {
                          const next = e.target.value;
                          setSkip2faReason(next);
                          // #586: clear the TL name when switching away from the
                          // TL-exemption reason so a stale/unused name is not
                          // forwarded.
                          if (next !== '2FA Exemption Approved by TL') {
                            setTeamLeadName('');
                            setTeamLeadNameError('');
                          }
                          // #789: re-arm the troubleshooting confirmation on any
                          // reason change so the agent must re-tick it for a new
                          // customer-device selection.
                          if (!requiresTroubleshootingConfirmation(next)) {
                            setTroubleshootingDone(false);
                          }
                        }}
                        aria-label="Reason for skipping two-factor authentication"
                        className="w-full max-w-[420px] h-[44px] px-3 border-2 border-[#dad7d3] rounded text-[16px] font-['Arial',sans-serif] text-[#1d252c] bg-white disabled:bg-[#f3f4f6] disabled:text-[#6b7280]"
                      >
                        <option value="">Select reason code</option>
                        {availableSkip2faReasons.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      {/* #586: the "2FA Exemption Approved by TL" reason requires
                          the approving team lead's name — validated the same way
                          as the Mobile Number Update team-lead field. The skip
                          does not activate (and completion is blocked) until a
                          valid name is entered; the name is recorded server-side
                          in the audited skip record. */}
                      {exemptionApprovedByTl && (
                        <div className="mt-3">
                          <label
                            htmlFor="skip-2fa-team-lead-name"
                            className="block text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                          >
                            Team lead&apos;s name
                          </label>
                          <input
                            id="skip-2fa-team-lead-name"
                            type="text"
                            value={teamLeadName}
                            maxLength={40}
                            disabled={otpStatus === 'pass' || otpBlocked}
                            aria-invalid={teamLeadNameError !== ''}
                            aria-describedby={
                              teamLeadNameError
                                ? 'skip-2fa-team-lead-name-error'
                                : undefined
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              setTeamLeadName(v);
                              setTeamLeadNameError(
                                v.trim() && !TL_NAME_ALLOWED.test(v.trim())
                                  ? "Team lead's name may contain only letters, spaces, apostrophes and hyphens."
                                  : '',
                              );
                            }}
                            className="w-full max-w-[420px] h-[44px] px-3 border-2 border-[#dad7d3] rounded text-[16px] font-['Arial',sans-serif] text-[#1d252c] bg-white disabled:bg-[#f3f4f6] disabled:text-[#6b7280]"
                          />
                          {teamLeadNameError && (
                            <p
                              id="skip-2fa-team-lead-name-error"
                              role="alert"
                              className="text-[14px] font-['Arial',sans-serif] text-[#b91c1c] mt-2 mb-0"
                            >
                              {teamLeadNameError}
                            </p>
                          )}
                        </div>
                      )}
                      {/* #789: the "2FA Customer Device Issue" reason requires
                          the agent to confirm troubleshooting was completed with
                          the customer. The skip does not activate until this box
                          is ticked. */}
                      {customerDeviceIssue && (
                        <div className="mt-3">
                          <label
                            htmlFor="skip-2fa-troubleshooting-done"
                            className="flex items-center gap-2 text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                          >
                            <input
                              id="skip-2fa-troubleshooting-done"
                              type="checkbox"
                              checked={troubleshootingDone}
                              disabled={otpStatus === 'pass' || otpBlocked}
                              onChange={(e) =>
                                setTroubleshootingDone(e.target.checked)
                              }
                              className="h-5 w-5"
                            />
                            Troubleshooting completed
                          </label>
                        </div>
                      )}
                      {skip2faActive && (
                        <p
                          role="status"
                          className="text-[14px] font-['Arial',sans-serif] font-bold text-[#15803d] mt-3 mb-0"
                        >
                          2FA skipped — reason recorded: {skip2faReason}.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-4">
                    <Button
                      type="button"
                      onClick={() => void handleGenerateOtp()}
                      disabled={
                        generating ||
                        otpStatus === 'pass' ||
                        otpBlocked ||
                        // #287: once the agent has selected a skip reason the OTP
                        // path is bypassed — do not offer generating a code.
                        skip2faActive ||
                        // #280: no fail-open — the code is never sent until the
                        // current customer's mobile has loaded and is on file.
                        !mobileReady
                      }
                      aria-label="Generate one-time passcode"
                      className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                    >
                      {generating
                        ? 'Generating…'
                        : otpSent
                          ? 'Resend OTP'
                          : 'Send Code'}
                    </Button>
                  </div>

                  {/* Send Code (generate) failure — gateway code + message
                  shown INLINE directly under the Send Code button (#782), so
                  the error is obvious right where the agent clicked. Validate
                  failures render under the Confirm button instead (below). */}
                  {otpError &&
                    otpError.source === 'generate' &&
                    otpStatus !== 'pass' &&
                    !otpExpired &&
                    !otpBlocked && (
                      <p
                        role="alert"
                        className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                      >
                        {otpError.code
                          ? `(${otpError.code}) ${otpError.message}`
                          : otpError.message}
                      </p>
                    )}

                  <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mt-4 mb-4">
                    I just sent the code to your phone. When you get it, can you
                    read it back to me?
                  </p>

                  <label
                    htmlFor="otp-code"
                    className="block text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2"
                  >
                    Verification number
                  </label>

                  <div className="flex items-center gap-4 flex-wrap">
                    <input
                      id="otp-code"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      placeholder="Enter verification number"
                      value={otpCode}
                      disabled={
                        !otpSent || otpStatus === 'pass' || skip2faActive
                      }
                      onChange={(e) =>
                        setOtpCode(
                          e.target.value.replace(/[^0-9]/g, '').slice(0, 6),
                        )
                      }
                      style={{ width: '220px' }}
                      className="px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#6b7280] transition-all focus:outline-none focus:ring-4 focus:border-[#005fcc] focus:ring-[#005fcc]/20 disabled:bg-[#f3f4f6] disabled:text-[#6b7280] disabled:cursor-not-allowed hover:border-[#979c9f] border-[#dad7d3]"
                    />

                    <Button
                      type="button"
                      onClick={() => void handleValidateOtp()}
                      disabled={
                        !otpSent ||
                        otpCode.length !== 6 ||
                        validating ||
                        otpStatus === 'pass' ||
                        skip2faActive
                      }
                      aria-label="Validate one-time passcode"
                      className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                    >
                      {validating ? 'Validating…' : 'Confirm'}
                    </Button>
                  </div>

                  {/* Validate (Confirm) failure — a wrong (non-final) code:
                  gateway code + message shown INLINE directly under the Confirm
                  button (#782), where the agent just clicked. Stays retryable.
                  The expired / blocked branches render their own alerts below. */}
                  {otpError &&
                    otpError.source === 'validate' &&
                    otpStatus !== 'pass' &&
                    !otpExpired &&
                    !otpBlocked && (
                      <p
                        role="alert"
                        className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                      >
                        {otpError.code
                          ? `Incorrect code (${otpError.code}): ${otpError.message}`
                          : otpError.message}
                      </p>
                    )}

                  {otpSent &&
                    !otpBlocked &&
                    !otpExpired &&
                    otpStatus !== 'pass' && (
                      <p
                        role="status"
                        aria-live="polite"
                        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mt-3 mb-0"
                      >
                        {`OTP attempts remaining: ${otpAttemptsRemaining}`}
                      </p>
                    )}
                  {otpExpired && (
                    <p
                      role="status"
                      className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                    >
                      {otpExpiredMessage ??
                        'The one-time passcode has expired. Generate a new code to continue.'}
                    </p>
                  )}
                  {otpBlocked && (
                    <p
                      role="alert"
                      className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                    >
                      The one-time passcode was entered incorrectly 3 times. OTP
                      generation is blocked for 24 hours — advise the customer
                      to visit a branch and add a customer note.
                    </p>
                  )}
                </>
              )}
            </div>
          </InfoBox>
        )}

        {/* Overall security outcome (CCF-57/59/60): the flag reflects the
            WHOLE verification — PASS only when every identity check AND 2FA
            have passed, FAIL if any Pass/Fail question fails or 2FA fails
            (computed LIVE via anyFailed, #840 — correcting a mis-clicked Fail
            back to Pass recovers the run; a genuine OTP lockout stays failed).
            It must NOT represent the 2FA result
            alone. #456: rendered OUTSIDE the OTP-entry branch so a
            carried-over 2FA flow (which replaces the OTP block with the
            "already completed" indicator) still shows the final PASS/FAIL
            outcome. #796: rendered OUTSIDE the `config.requires2fa` block
            too, so the NON-2FA variant (Authorised VISA User, SCRN-06 /
            CCF-25) — whose OTP InfoBox never renders — still shows the
            PASS/FAIL outcome once every identity check is answered. */}
        <div className="flex items-center gap-4 flex-wrap">
          {allChecksPass && (
            <span
              className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#15803d] text-white border-2 border-[#15803d]"
              aria-label="Verification passed"
            >
              PASS
            </span>
          )}
          {/* #263: only presented once every step (incl. 2FA) is done. */}
          {showFail && (
            <span
              className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#dc2626] text-white border-2 border-[#dc2626]"
              aria-label="Verification failed"
            >
              FAIL
            </span>
          )}
        </div>

        {/* #659 (@supervisor, CCRS-3263): the "cannot complete 2FA" affordance on
            the VISA/Business Medium screens (SCRN-04/05) is rendered as a SEPARATE
            element BELOW the 2FA section — a sibling of the OTP InfoBox, not nested
            inside it — with the bold "Proceed to high security?" heading, matching
            the EDS Medium (`MediumSecurityScreen.tsx`) and ECL Medium
            (`EclMediumSecurityScreen.tsx`) siblings. #287 (@dylan219 "Option A"):
            on these medium-security screens the 2FA step cannot be skipped; when
            the customer cannot complete 2FA the agent transitions to this page's
            own relevant HIGH-security page (where the skip-with-reason dropdown
            lives) via this direct navigation control — the same existing RBAC-gated
            route also offered in the process menu. Emphasised when no mobile is on
            file (the reported dead-end). Offered on medium-2FA variants only (ECL
            medium is a separate component). Layout/copy parity only — the #634
            below-2FA position, the skip-with-reason logic and the OTP handshake are
            unchanged. */}
        {mediumHighTransition && (
          <div
            className={`rounded-md border-2 p-4 ${mobileMissing ? 'border-[#FF703E] bg-[#FFF4EF]' : 'border-[#dad7d3] bg-[#faf9f7]'}`}
          >
            <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mt-0 mb-2">
              Proceed to high security?
            </p>
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mt-0 mb-3">
              If higher-level verification is required, transition to{' '}
              {mediumHighTransition.highLabel}.
            </p>
            <Button
              type="button"
              onClick={() => navigate(mediumHighTransition.highRoute)}
              aria-label={`Go to ${mediumHighTransition.highLabel}`}
              className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
            >
              Go to {mediumHighTransition.highLabel}
            </Button>
          </div>
        )}

        {/* #756 (Option A, @dylan219): the customer does not have enough
            security answers on file to pass the `askAll` knowledge questions
            (#722's ≥N-answers requirement can never be met). Rather than the
            previous silent dead-end (all radios Pass, but no Continue button and
            no verdict), tell the agent explicitly that High Security cannot be
            completed via these questions and they must Fail / refer the customer
            to branch. Shown until the agent records a Fail (then the branch-
            referral guidance below takes over). No gate is changed. */}
        {questionsLoaded &&
          knowledgeInsufficientOnFile &&
          !allChecksPass &&
          !showFail && (
            <div
              role="status"
              aria-live="polite"
              className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3"
            >
              <img
                src={infoIcon}
                alt=""
                aria-hidden="true"
                className="w-5 h-5 mt-1 shrink-0"
              />
              <div>
                <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
                  High Security cannot be completed with these questions.
                </p>
                <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                  This customer does not have enough security answers on file to
                  pass the knowledge questions. Mark a knowledge question as
                  Fail and refer the customer to branch — do not continue to the
                  dashboard.
                </p>
              </div>
            </div>
          )}

        {/* Branch-referral guidance when a check has failed (CCF-22). Driven by
            the LIVE anyFailed (#840) so flipping a mis-clicked Fail radio back to
            Pass clears it; a genuine OTP lockout (included in anyFailed) keeps it.
            #263: only presented once ALL steps (incl. 2FA where required) are
            complete, not on the first failed answer while a later step is still
            outstanding. */}
        {showFail && (
          <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3">
            <img
              src={infoIcon}
              alt=""
              aria-hidden="true"
              className="w-5 h-5 mt-1 shrink-0"
            />
            <div>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-4 whitespace-pre-line">
                {failHeading}
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 whitespace-pre-line">
                {failNote}
              </p>
            </div>
          </div>
        )}

        {/* Issues #369 / #370: "verification not yet complete" guidance. When
            the agent has started but there is no verdict and no Continue button,
            spell out the outstanding steps so the screen is never a silent
            dead-end (e.g. a no-mobile customer whose mobile question still needs
            its "not on file" answer, or a mortgage run missing its 2-of-7
            selections). Guidance only — no gate is changed. */}
        {showOutstanding && (
          <div
            role="status"
            aria-live="polite"
            className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm"
          >
            <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-3">
              Verification is not yet complete. To finish and continue to the
              dashboard:
            </p>
            <ul className="list-disc pl-6 m-0 space-y-1">
              {outstandingSteps.map((step) => (
                <li
                  key={step}
                  className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]"
                >
                  {step}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Continue to Dashboard — gated on the LIVE result of THIS run, with
            verification committed on the explicit action (CCF-53/22). */}
        {allChecksPass && (
          <div className="flex">
            <Button
              type="button"
              onClick={() => void completeAndContinue()}
              aria-label="Continue to dashboard"
              className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
            >
              Continue to dashboard
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
